import { IndicatorTooltip, IndicatorDonut, IndicatorRanking, IndicatorStackShape } from './shared/IndicadoresVisuals';
import OpenFreeMapLayer from './shared/OpenFreeMapLayer';
import { PharmacySelect, PharmacyMetric, PharmacyChartCard, PharmacyPagination, PharmacyDownloadDialog, PharmacyTabs } from './shared/PharmacyUI';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Box, Typography, CircularProgress, Dialog, Tooltip, InputAdornment, useMediaQuery } from '@mui/material';
import { MapContainer, Marker, Tooltip as LeafletTooltip, useMap } from 'react-leaflet';
import { ComposedChart, Line, Area, Bar, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import L from 'leaflet';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';
import api from '../services/api';
import 'leaflet/dist/leaflet.css';
import { HeaderIOS, IOS as BaseIOS } from './shared/MenuLayout';

const IOS = { ...BaseIOS, blue:'#548EE6', green:'#59B989', orange:'#E6A35D', red:'#DE777E', purple:'#9982D8', separator:'#E9EDF3', label:'#263348' };

// ── Pines ─────────────────────────────────────────────────────────────────────
const makePin = (f) => L.icon({ iconUrl:`/Imagenes/${f}`, iconSize:[38,38], iconAnchor:[19,38], popupAnchor:[0,-42] });
const PIN_H = makePin('mapa_h.png'), PIN_CS = makePin('mapa_cs.png'), PIN_PS = makePin('mapa_ps.png');
L.Marker.prototype.options.icon = PIN_PS;
const getPin = (n='') => {
  const u = n.toUpperCase();
  if (u.includes('HOSPITAL')) return PIN_H;
  if (u.includes('C.S.')||u.includes('CENTRO DE SALUD')) return PIN_CS;
  return PIN_PS;
};
const MapCenter = ({ center }) => { const map = useMap(); useEffect(()=>{ if(center) map.setView(center,13); },[center,map]); return null; };

const IpressMarker = ({ ip, selected, onClick }) => {
  const ref = React.useRef(null);
  useEffect(() => {
    const m = ref.current;
    if (!m) return;
    if (selected) m.openTooltip();
    else m.closeTooltip();
  }, [selected]);
  return (
    <Marker ref={ref} position={[parseFloat(ip.ue_latitud),parseFloat(ip.ue_longitud)]} icon={getPin(ip.nombre)}
      eventHandlers={{ click: onClick }}>
      <LeafletTooltip direction="top" offset={[0,-42]} opacity={1} permanent={selected}>
        <span style={{ fontWeight:700, fontSize:'11px', color:IOS.blue }}>{ip.nombre}</span>
      </LeafletTooltip>
    </Marker>
  );
};

// ── Indicadores config (sistema unificado) ────────────────────────────────────
const IND_CFG = {
  'SOBRESTOCK':   { bg:'#D1FAE5', border:'#6EE7B7', text:'#065F46', dot:'#4DAE82' },
  'NORMOSTOCK':   { bg:'#DCFCE7', border:'#86EFAC', text:'#166534', dot:'#83BF98' },
  'SUBSTOCK':     { bg:'#FFEDD5', border:'#FED7AA', text:'#9A3412', dot:'#E6A35D' },
  'DESABASTECIDO':{ bg:'#FEE2E2', border:'#FCA5A5', text:'#991B1B', dot:'#DE777E' },
  'SIN ROTACION': { bg:'#F3F4F6', border:'#D1D5DB', text:'#4B5563', dot:'#A6B1C2' },
  'SIN CONSUMO':  { bg:'#E5E7EB', border:'#C4C9D4', text:'#374151', dot:'#8290A6' },
  'NO APLICA':    { bg:'#EDE9FE', border:'#C4B5FD', text:'#4C1D95', dot:'#7C3AED' },
};
const getInd = (v) => IND_CFG[typeof v==='string' ? v.toUpperCase() : ''] || { bg:IOS.bg, border:IOS.separator, text:IOS.label2, dot:IOS.label3 };

const dispCfg = (v) => v>=90?{ color:'#4DAE82', label:'ÓPTIMO' }:v>=80?{ color:'#83BF98', label:'BUENO' }:v>=70?{ color:'#E6A35D', label:'REGULAR' }:{ color:'#DE777E', label:'CRÍTICO' };

// ── Helpers ───────────────────────────────────────────────────────────────────
const rk = (obj,...keys) => { if(!obj) return 0; for(const k of keys) if(obj[k]!==undefined&&obj[k]!==null) return obj[k]; return 0; };
const makeR = (d) => (campo) => {
  if (!d) return 0;
  const V = {
    totalProductos:['totalProductos','TotalProductos'], totalME:['totalME','TotalME','totalMe'],
    sobreStock:['sobreStock','SobreStock'], porcSobre:['porc_SobreStock','Porc_SobreStock'],
    normoStock:['normoStock','NormoStock'], porcNormo:['porc_NormoStock','Porc_NormoStock'],
    subStock:['subStock','SubStock'],       porcSub:['porc_SubStock','Porc_SubStock'],
    desabastecido:['desabastecido','Desabastecido'], porcDesab:['porc_Desabaste','Porc_Desabaste'],
    sinRotacion:['sinRotacion','SinRotacion'], porcRot:['porc_SinRotacion','Porc_SinRotacion'],
    sinConsumo:['sinConsumo','SinConsumo'], porcConsumo:['porc_SinConsumo','Porc_SinConsumo'],
    disp:['disp_ME_2meses','Disp_ME_2meses'],
  };
  return rk(d,...(V[campo]||[campo]));
};
const T = (row,campo) => { if(!row) return ''; const V={ codigo:['codigO_MED','codigo_MED','CODIGO_MED'], producto:['producto','PRODUCTO'], consumo:['totalConsumo','TotalConsumo'], stock:['stock_Final','Stock_Final'], cpma:['cpma','CPMA'], msd:['msd','MSD'], ind:['indicador_DIGEMID','Indicador_DIGEMID'] }; return rk(row,...(V[campo]||[campo])); };
const getMes = (row,n) => { if(!row) return 0; for(const v of [`meS${n}`,`mes${n}`,`MES${n}`,`Mes${n}`]) if(row[v]!==undefined) return Number(row[v])||0; return 0; };
const fmtP = (p) => (!p||p.length<6)?'':`${p.substring(4,6)}/${p.substring(0,4)}`;
const periodos = () => { const list=[],now=new Date(); for(let i=11;i>=0;i--){ const f=new Date(now.getFullYear(),now.getMonth()-i,1); list.push(`${f.getFullYear()}${String(f.getMonth()+1).padStart(2,'0')}`); } return list; };
const mesLabels = (per) => { if(!per||per.length<6) return Array.from({length:12},(_,i)=>({yyyymm:`M${i+1}`,short:`M${i+1}`,isCurrent:i===11})); const y=parseInt(per.substring(0,4)),m=parseInt(per.substring(4,6)); return Array.from({length:12},(_,i)=>{ let mm=m-(11-i),yy=y; while(mm<=0){mm+=12;yy--;} const mm2=String(mm).padStart(2,'0'); return {yyyymm:`${yy}${mm2}`,short:`${mm2}/${String(yy).slice(2)}`,isCurrent:i===11}; }); };

// ── IndPill ───────────────────────────────────────────────────────────────────
const IndPill = ({ valor, sm }) => {
  const cfg = getInd(valor);
  return (
    <Box sx={{ display:'inline-flex', alignItems:'center', gap:0.5,
      px:sm?0.7:1, py:sm?0.15:0.3, borderRadius:'20px',
      bgcolor:cfg.bg, border:`1px solid ${cfg.border}` }}>
      <Box sx={{ width:sm?4:5, height:sm?4:5, borderRadius:'50%', bgcolor:cfg.dot }} />
      <Typography sx={{ fontSize:sm?'0.58rem':'0.65rem', fontWeight:700, color:cfg.text, whiteSpace:'nowrap' }}>
        {valor||'—'}
      </Typography>
    </Box>
  );
};

const MiniSparkline = ({ meses }) => {
  const max = Math.max(...meses, 1);
  return (
    <Box className="pharmacy-layout" sx={{ display:'flex', alignItems:'flex-end', gap:'2px', height:24, width:84, mx:'auto' }}>
      {meses.map((v,i)=>{
        const pct = max>0 ? (v/max)*100 : 0;
        const isCur = i===11;
        return (
          <Box key={i} sx={{ flex:1, minHeight: v===0?1:2,
            height:`${Math.max(pct, v===0?3:8)}%`,
            bgcolor: v===0?IOS.separator:isCur?IOS.blue:`${IOS.blue}50`,
            borderRadius:'1px 1px 0 0' }} />
        );
      })}
    </Box>
  );
};

// ── Sel ───────────────────────────────────────────────────────────────────────
const Sel = PharmacySelect;

// ── Tooltip dark recharts ─────────────────────────────────────────────────────
const TT = IndicatorTooltip;

// ── KPI strip card ────────────────────────────────────────────────────────────
const KpiStrip=({label,valor,porc,color,icon,esRef,esJurisdiccion})=><PharmacyMetric label={label+(esRef?' (ref)':'')} valor={valor} color={esRef?'#8E98A8':color} icon={icon} reference={esRef} progress={porc} sub={Number(porc||0).toFixed(1)+'% '+(!esRef&&esJurisdiccion?'de evaluados':'del total')}/>;

// ── Card gráfico wrapper ──────────────────────────────────────────────────────
const ChartCard = PharmacyChartCard;

const PaginacionICI = PharmacyPagination;

// ── Fila de la tabla ICI (memoizada — solo re-renderiza si SUS props cambian) ─
const FilaICI = React.memo(function FilaICI({ row, idx, exp, mLabels, onToggle, onVerMed }) {
  const ind = T(row,'ind');
  const msd = Number(T(row,'msd')||0);
  const meses = [1,2,3,4,5,6,7,8,9,10,11,12].map(n=>getMes(row,n));
  const maxM = Math.max(...meses,1);
  return (
    <React.Fragment>
      <tr style={{ borderBottom:exp?'none':`1px solid ${IOS.separator}`,
        cursor:'pointer', backgroundColor:exp?`${IOS.blue}06`:IOS.surface,
        transition:'background 0.1s' }}
        onClick={()=>onToggle(idx)}
        onMouseEnter={e=>{ if(!exp) e.currentTarget.style.backgroundColor=IOS.bg; }}
        onMouseLeave={e=>{ e.currentTarget.style.backgroundColor=exp?`${IOS.blue}06`:IOS.surface; }}>
        <td style={{ padding:'4px 8px', textAlign:'center' }}>
          <Box sx={{ width:18, height:18, borderRadius:'6px',
            bgcolor:exp?IOS.blue:'#E5E5EA', display:'inline-flex',
            alignItems:'center', justifyContent:'center', transition:'all 0.15s' }}>
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none"
              style={{ transform:exp?'rotate(180deg)':'none', transition:'transform 0.2s' }}>
              <path d="M6 9l6 6 6-6" stroke={exp?'white':'#636366'} strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
          </Box>
        </td>
        <td style={{ padding:'10px 10px', fontSize:'0.72rem', color:IOS.label2, fontFamily:'monospace' }}>{T(row,'codigo')}</td>
        <td style={{ padding:'10px 10px', fontSize:'0.76rem', color:IOS.label, maxWidth:220 }}>
          <Tooltip title={T(row,'producto')} placement="top">
            <span style={{ display:'block', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:220 }}>{T(row,'producto')}</span>
          </Tooltip>
        </td>
        <td style={{ padding:'10px 10px', textAlign:'right', fontWeight:600, color:IOS.label }}>{T(row,'consumo')||0}</td>
        <td style={{ padding:'10px 10px', textAlign:'right', fontWeight:700, color:IOS.label }}>{Number(T(row,'stock')||0).toFixed(0)}</td>
        <td style={{ padding:'10px 10px', textAlign:'right', color:IOS.label }}>{Number(T(row,'cpma')||0).toFixed(2)}</td>
        <td style={{ padding:'10px 10px', textAlign:'right', color:IOS.label }}>{msd.toFixed(2)}</td>
        <td style={{ padding:'10px 10px', textAlign:'center' }}>
          <Tooltip title={`Consumo mensual: ${meses.join(' · ')}`} placement="top">
            <Box sx={{ display:'inline-block' }}><MiniSparkline meses={meses} /></Box>
          </Tooltip>
        </td>
        <td style={{ padding:'10px 10px', textAlign:'center' }}>
          <IndPill valor={ind} sm />
        </td>
        <td style={{ padding:'10px 8px', textAlign:'center' }}>
          <Box component="button" type="button" aria-label="Ver evolución del medicamento" onClick={e=>{ e.stopPropagation(); onVerMed(T(row,'codigo'),T(row,'producto')); }}
            sx={{ border:0, p:0, width:32, height:32, borderRadius:'50%',
              bgcolor:`${IOS.blue}12`, display:'inline-flex',
              alignItems:'center', justifyContent:'center', cursor:'pointer',
              transition:'all 0.15s', '&:hover':{ bgcolor:IOS.blue, '& svg':{ color:'white' } } }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2" strokeLinecap="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
            </svg>
          </Box>
        </td>
      </tr>

      {exp && (
        <tr style={{ borderBottom:`1px solid ${IOS.separator}` }}>
          <td colSpan={9} style={{ padding:0, backgroundColor:IOS.bg }}>
            <Box sx={{ px:3, py:2 }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:IOS.label2,
                mb:1.5, textTransform:'uppercase', letterSpacing:'0.06em' }}>
                Consumo mensual — {T(row,'producto')}
              </Typography>
              <Box className="pharmacy-layout" sx={{ display:'flex', gap:1, alignItems:'flex-end', overflowX:'auto', pb:0.5 }}>
                {meses.map((val,mi)=>{
                  const pct=maxM>0?(val/maxM)*100:0;
                  const ml=mLabels[mi];
                  const cur=ml?.isCurrent;
                  return (
                    <Box className="pharmacy-layout" key={mi} sx={{ display:'flex', flexDirection:'column', alignItems:'center', flex:1, minWidth:36 }}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight:600,
                        color:val===0?IOS.label3:IOS.label, mb:0.5 }}>{val}</Typography>
                      <Box className="pharmacy-layout" sx={{ width:'70%', height:44, display:'flex', alignItems:'flex-end', justifyContent:'center' }}>
                        <Box sx={{ width:'100%', minHeight:val===0?0:3,
                          height:`${Math.max(pct,val===0?0:5)}%`,
                          bgcolor:val===0?IOS.separator:cur?IOS.blue:`${IOS.blue}60`,
                          borderRadius:'4px 4px 0 0', transition:'height 0.3s' }} />
                      </Box>
                      <Typography sx={{ fontSize: '0.72rem', color:IOS.label2, mt:0.4,
                        textAlign:'center', whiteSpace:'nowrap', fontWeight:cur?700:400 }}>
                        {ml?.short||`M${mi+1}`}
                      </Typography>
                      {cur && <Box sx={{ width:5, height:5, borderRadius:'50%', bgcolor:IOS.blue, mt:0.3 }} />}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
});

// ── Modal visor PDF ────────────────────────────────────────────────────────
const ModalPdfVisorICI = ({ open, onClose, pdfUrl, cargando, titulo, subtitulo, nombreDescarga }) => {
  if (!open) return null;
  return (
    <Box className="indicadores-page pharmacy-page pharmacy-overlay" sx={{ position:'fixed', inset:0, zIndex:9999, bgcolor:'rgba(0,0,0,0.7)',
      display:'flex', alignItems:'center', justifyContent:'center', p:2, backdropFilter:'blur(4px)' }}
      onClick={onClose}>
      <Box className="pharmacy-modal-panel pharmacy-layout pharmacy-card" onClick={e=>e.stopPropagation()}
        sx={{ bgcolor:IOS.surface, borderRadius:'20px', overflow:'hidden',
          width:'92vw', maxWidth:1100, height:'92vh',
          display:'flex', flexDirection:'column', boxShadow:'0 24px 60px rgba(0,0,0,0.4)' }}>
        <Box className="pharmacy-layout" sx={{ px:2.5, pt:2, pb:1.5, borderBottom:`1px solid ${IOS.separator}`, flexShrink:0,
          display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <Box>
            <Typography sx={{ fontWeight:700, fontSize:'0.9rem', color:IOS.label }}>{titulo}</Typography>
            {subtitulo && <Typography sx={{ fontSize:'0.72rem', color:IOS.label2 }}>{subtitulo}</Typography>}
          </Box>
          <Box className="pharmacy-layout" sx={{ display:'flex', gap:1, alignItems:'center' }}>
            {pdfUrl && (
              <>
                <Box className="pharmacy-layout" component="a" href={pdfUrl} download={nombreDescarga}
                  sx={{ height:32, px:1.5, borderRadius:'10px', border:`1px solid ${IOS.green}30`,
                    bgcolor:`${IOS.green}08`, cursor:'pointer', color:IOS.green, fontWeight:700,
                    fontSize:'0.78rem', textDecoration:'none', display:'flex', alignItems:'center',
                    gap:0.5, '&:hover':{ bgcolor:`${IOS.green}14` } }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={IOS.green} strokeWidth="2" strokeLinecap="round"><path d="M12 3v13M7 11l5 5 5-5"/><path d="M4 20h16"/></svg>
                  Descargar
                </Box>
                <Box className="pharmacy-layout pharmacy-action" component="button"
                  onClick={() => {
                    const iframe = document.getElementById('pdf-visor-frame-ici');
                    if (iframe) { iframe.contentWindow.focus(); iframe.contentWindow.print(); }
                  }}
                  sx={{ height:32, px:1.5, borderRadius:'10px', border:`1px solid ${IOS.blue}30`,
                    bgcolor:`${IOS.blue}08`, cursor:'pointer', display:'flex', alignItems:'center',
                    gap:0.5, '&:hover':{ bgcolor:`${IOS.blue}14` } }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2" strokeLinecap="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                  <Typography sx={{ fontSize:'0.78rem', fontWeight:700, color:IOS.blue }}>Imprimir</Typography>
                </Box>
              </>
            )}
            <Box className="pharmacy-layout pharmacy-action" component="button" onClick={onClose}
              sx={{ width:30, height:30, borderRadius:'50%', border:'none', bgcolor:'#E5E5EA',
                cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
                '&:hover':{ bgcolor:'#D1D1D6' } }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={IOS.label} strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </Box>
          </Box>
        </Box>
        <Box sx={{ flex:1, overflow:'hidden', bgcolor:'#525659' }}>
          {cargando
            ? <Box className="pharmacy-layout" sx={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100%', gap:2 }}>
                <CircularProgress sx={{ color:'white' }} />
                <Typography sx={{ color:'rgba(255,255,255,0.8)', fontSize:'0.85rem' }}>Generando reporte...</Typography>
              </Box>
            : pdfUrl
              ? <iframe id="pdf-visor-frame-ici" src={pdfUrl}
                  style={{ width:'100%', height:'100%', border:'none' }} title="Reporte PDF" />
              : <Box className="pharmacy-layout" sx={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%' }}>
                  <Typography sx={{ color:'rgba(255,255,255,0.6)' }}>No se pudo generar el reporte</Typography>
                </Box>
          }
        </Box>
      </Box>
    </Box>
  );
};

// ── Modal selector de formato (PDF ver / Excel descargar) ─────────────────
const ModalExportarICI = ({ open, onClose, total, onExportarExcel, onVerPdf, exportando }) => (<PharmacyDownloadDialog open={open} onClose={onClose} total={total} busy={exportando} options={[{fmt:'pdf',label:'Ver PDF',sub:'Previsualizar e imprimir',onClick:onVerPdf},{fmt:'excel',label:'Descargar Excel',onClick:onExportarExcel}]}/>);

// ═════════════════════════════════════════════════════════════════════════════
const Indicadores = () => {
  const navigate = useNavigate();
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const compactCharts = useMediaQuery('(max-width:899px)');
  const user     = authService.getCurrentUser();
  const ueId     = user?.ueIdEstablecimiento || user?.UeIdEstablecimiento || 0;
  const lat0     = user?.UeLatitud  || -13.4215;
  const lng0     = user?.UeLongitud || -76.1358;
  const idEstabUsuario = String(user?.idEstablecimiento || user?.IdEstablecimiento || 0);

  const [privInd, setPrivInd] = useState(null);
  useEffect(()=>{
    api.get('/dashboard/permisos').then(r=>{
      if(r.data.success){
        const op = r.data.opciones.find(o=>(o.permiso||'').toLowerCase().includes('indicador'));
        if(op) setPrivInd(op.privilegio||'Solo lectura');
      }
    }).catch(()=>{});
  },[]);
  const esTOTAL = privInd === 'Total';

  const [tab, setTab]                 = useState(0);
  const [ipressList, setIpressList]   = useState([]);
  const [ipressMarkers, setIpressMarkers] = useState([]);
  const [ipressSel, setIpressSel]     = useState(null);
  const [mapCenter, setMapCenter]     = useState([lat0, lng0]);
  const [periodo, setPeriodo]         = useState('');
  const [topN, setTopN]               = useState(10);
  const [loadingIP, setLoadingIP]     = useState(true);

  const [disponibilidad, setDisp]     = useState([]);
  const [desabastecidos, setDesab]    = useState([]);
  const [substockTop, setSubTop]      = useState([]);
  const [loadG, setLoadG]             = useState(false);

  const [resumen, setResumen]         = useState(null);
  const [tabla, setTabla]             = useState([]);
  const [tablaF, setTablaF]           = useState([]);
  const [busqueda, setBusqueda]       = useState('');
  const [busquedaDeb, setBusquedaDeb] = useState('');
  useEffect(()=>{ const t=setTimeout(()=>setBusquedaDeb(busqueda),300); return ()=>clearTimeout(t); },[busqueda]);
  const [filtroInd, setFiltroInd]     = useState('TODOS');
  const [filaExp, setFilaExp]         = useState(null);
  const [pgICI, setPgICI]             = useState(1);
  const [rppICI, setRppICI]           = useState(50);
  const [vistaTipo, setVistaTipo]     = useState('ME');
  const [loadRes, setLoadRes]         = useState(false);

const [modal, setModal]             = useState(false);
  const [medSel, setMedSel]           = useState(null);
  const [dataMed, setDataMed]         = useState([]);
  const [loadModal, setLoadModal]     = useState(false);

  const ipressActual = ipressList.find(i=>i.nombre===ipressSel);
  const esHospitalFarmacias = ipressActual?.nombre==='FARMACIAS DEL HOSPITAL';
  const esTodosUE = ipressActual?.nombre==='TODOS';

  const handleLogout = () => { authService.logout(); navigate('/login'); };

  const [gerencial, setGerencial] = useState([]);
  const [loadGer, setLoadGer] = useState(false);
  const [selEvol, setSelEvol] = useState(null);
  const [busquedaGer, setBusquedaGer] = useState('');
  const [evolData, setEvolData] = useState([]);
  const [loadEvol, setLoadEvol] = useState(false);

  const evolReqId = useRef(0);
  const cargarEvolucionIpress = async (id, nombre, esHosp) => {
    const miId = ++evolReqId.current;
    setSelEvol({ id, nombre, esHospital: esHosp }); setLoadEvol(true); setEvolData([]);
    try {
      const r = await api.get('/farmacia/indicadores/evolucion-ipress', { params:{ periodoRef:periodo, idUnidadEjecutora:ueId, idEstablecimiento:id, nombre, esHospital:esHosp } });
      if (miId !== evolReqId.current) return; // se hizo clic en otra fila mientras esta cargaba — se descarta
      if (r.data.success) setEvolData(r.data.data.map(x=>({ periodo:x.periodo, disp:x.data.disp_ME_2meses, sobrestock:x.data.sobrestock, normostock:x.data.normostock, substock:x.data.substock, desabastecido:x.data.desabastecido })));
    } catch(e) { console.error(e); } finally { if (miId === evolReqId.current) setLoadEvol(false); }
  };
  const gerReqId = useRef(0);
  useEffect(() => {
    if (tab!==2 || !esTodosUE || !periodo) return;
    const miId = ++gerReqId.current;
    setLoadGer(true);
    api.get('/farmacia/indicadores/resumen-por-ipress', { params:{ periodoRef:periodo, idUnidadEjecutora:ueId } })
      .then(r=>{
        if (miId !== gerReqId.current) return;
        if(r.data.success) setGerencial(r.data.data);
      })
      .catch(console.error)
      .finally(()=>{ if (miId === gerReqId.current) setLoadGer(false); });
  }, [tab, esTodosUE, periodo, ueId]);

  const [modalAuditoria, setModalAuditoria] = useState(false);
  const [loadAuditoria, setLoadAuditoria]   = useState(false);
  const [auditConsultado, setAuditConsultado] = useState(false);
  const [auditTab, setAuditTab]         = useState(0);
  const [auditFaltantes, setAuditFaltantes] = useState([]);
  const [auditAnomalias, setAuditAnomalias] = useState([]);
  const [auditEsUE, setAuditEsUE]       = useState(false);



  useEffect(() => {
    if(!privInd) return;
    const m = new Date().getMonth();
    const y = new Date().getFullYear();
    setPeriodo(m===0?`${y-1}12`:`${y}${String(m).padStart(2,'0')}`);
    setLoadingIP(true);
    api.get(`/farmacia/indicadores/listar-ipress-mapa?ueId=${ueId}`)
      .then(r => {
        if(r.data.success) {
          // Lista completa para el combo — respeta el orden del SP, incluye TODOS
          const full = r.data.data.filter(i=>i.nombre && i.nombre.toUpperCase()!=='UNIDAD EJECUTORA');
          // Subconjunto con coordenadas — solo para pintar pines en el mapa
          const conCoords = full.filter(i=>i.ue_latitud&&i.ue_longitud);
          if(full.length>0){
            const miIpress = conCoords.find(ip=>String(ip.id_establecimiento)===idEstabUsuario) || conCoords[0] || full[0];
            const listaFinal = privInd==='Total' ? full : [miIpress];
            setIpressList(listaFinal);
            setIpressMarkers(conCoords);
            setIpressSel(miIpress.nombre);
            if(miIpress.ue_latitud) setMapCenter([parseFloat(miIpress.ue_latitud),parseFloat(miIpress.ue_longitud)]);
          }
        }
      }).catch(console.error).finally(()=>setLoadingIP(false));
  }, [ueId, privInd]);

  useEffect(() => {
    if(!ipressSel||!periodo) return;
    if(tab===0) cargarDashboard();
    else cargarDetalles();
  }, [ipressSel, periodo, topN, tab, vistaTipo]);

  useEffect(() => {
    let f = tabla;
    if(busquedaDeb) { const b=busquedaDeb.toLowerCase(); f=f.filter(r=>(T(r,'codigo')||'').toLowerCase().includes(b)||(T(r,'producto')||'').toLowerCase().includes(b)); }
    if(filtroInd!=='TODOS') {
      if(esTodosUE) {
        const campo = { SOBRESTOCK:'sobrestock', NORMOSTOCK:'normostock', SUBSTOCK:'substock', DESABASTECIDO:'desabastecido', 'SIN ROTACION':'sinRotacion', 'SIN CONSUMO':'sinConsumo' }[filtroInd];
        f = campo ? f.filter(r=>Number(r[campo]||0)>0) : [];
      } else {
        f=f.filter(r=>(T(r,'ind')||'').toUpperCase()===filtroInd);
      }
    }
    setTablaF(f);
  }, [busquedaDeb, filtroInd, tabla, esTodosUE]);
  useEffect(() => { setFilaExp(null); }, [pgICI]);

  const cargarDashboard = async () => {
    setLoadG(true);
    try {
      const idEstab = ipressActual?.id_establecimiento;
      const paramsExtra = esTodosUE ? { idUnidadEjecutora: ueId, alcance: 'todos' }
        : esHospitalFarmacias ? { idUnidadEjecutora: ueId, alcance: 'hospital' }
        : { idEstablecimiento: idEstab };
      const [rD, rDes, rSub] = await Promise.all([
        api.get('/farmacia/indicadores/disponibilidad-me-anual',{ params:{ periodoRef:periodo, ...paramsExtra } }),
        api.get('/farmacia/indicadores/top-desabastecidos',{ params:{ periodoRef:periodo, ...paramsExtra, topN } }),
        api.get('/farmacia/indicadores/substock-riesgo',{ params:{ periodoRef:periodo, ...paramsExtra, topN } }),
      ]);
      if(rD.data.success) setDisp(rD.data.periodos.map((p,i)=>({ periodo:p, valor:rD.data.valores[i] })));
      if(rDes.data.success) setDesab(rDes.data.productos.map((p,i)=>({ producto:p.length>32?p.substring(0,32)+'…':p, cpma:rDes.data.valores[i] })));
      if(rSub.data.success) setSubTop(rSub.data.productos.map((p,i)=>({ producto:p.length>32?p.substring(0,32)+'…':p, msd:rSub.data.valores[i] })));
    } catch(e) { console.error(e); } finally { setLoadG(false); }
  };

  const cargarDetalles = async () => {
    setLoadRes(true); setResumen(null); setTabla([]); setTablaF([]);
    try {
      const idEstab = ipressActual?.id_establecimiento;
      const [rR, rT] = esTodosUE
        ? await Promise.all([
            api.get('/farmacia/indicadores/resumen-ici-todos',{ params:{ periodoRef:periodo, idUnidadEjecutora:ueId } }),
            api.get('/farmacia/indicadores/ici-mensual-todos',{ params:{ periodoRef:periodo, idUnidadEjecutora:ueId, tipo:vistaTipo } }),
          ])
        : esHospitalFarmacias
        ? await Promise.all([
            api.get('/farmacia/indicadores/resumen-ici-hospital',{ params:{ periodoRef:periodo, idUnidadEjecutora:ueId } }),
            api.get('/farmacia/indicadores/ici-mensual-hospital',{ params:{ periodoRef:periodo, idUnidadEjecutora:ueId, tipo:vistaTipo } }),
          ])
        : await Promise.all([
            api.get('/farmacia/indicadores/resumen-ici',{ params:{ periodoRef:periodo, idEstablecimiento:idEstab } }),
            api.get('/farmacia/indicadores/ici-mensual',{ params:{ periodoRef:periodo, idEstablecimiento:idEstab, tipo:vistaTipo } }),
          ]);
      if(rR.data.success) setResumen(rR.data.data);
      if(rT.data.success) { setTabla(rT.data.data); setTablaF(rT.data.data); }
    } catch(e) { console.error(e); } finally { setLoadRes(false); }
  };

  const toggleFila = useCallback((idx) => setFilaExp(prev => prev===idx?null:idx), []);

  const [modalExpICI, setModalExpICI] = useState(false);
  const [modalPdfICI, setModalPdfICI] = useState(false);
  const [pdfUrlICI, setPdfUrlICI]     = useState(null);
  const [cargPdfICI, setCargPdfICI]   = useState(false);
  const [expExcelICI, setExpExcelICI] = useState(false);

  const paramsExportICI = () => {
    const params = { periodoRef: periodo, tipo: vistaTipo, ipressNombre: ipressActual?.nombre || '' };
    if (esTodosUE) { params.idUnidadEjecutora = ueId; params.alcance = 'todos'; }
    else if (esHospitalFarmacias) { params.idUnidadEjecutora = ueId; params.alcance = 'hospital'; }
    else { params.idEstablecimiento = ipressActual?.id_establecimiento; }
    return params;
  };

  const verPdfICI = async () => {
    if (cargPdfICI) return;
    setModalExpICI(false); setPdfUrlICI(null); setCargPdfICI(true); setModalPdfICI(true);
    try {
      const r = await api.get('/farmacia/indicadores/exportar-pdf', { params: paramsExportICI(), responseType:'blob' });
      setPdfUrlICI(URL.createObjectURL(new Blob([r.data], { type:'application/pdf' })));
    } catch(e) { console.error(e); setModalPdfICI(false); } finally { setCargPdfICI(false); }
  };

  const exportarExcelICI = async () => {
    if (expExcelICI) return;
    setExpExcelICI(true);
    try {
      const r = await api.get('/farmacia/indicadores/exportar-excel', { params: paramsExportICI(), responseType:'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([r.data], { type:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      a.download = `ICI_${periodo}_${vistaTipo}.xlsx`;
      a.click(); URL.revokeObjectURL(a.href);
      setModalExpICI(false);
    } catch(e) { console.error(e); } finally { setExpExcelICI(false); }
  };

  const closePdfICI = () => { setModalPdfICI(false); if (pdfUrlICI) { URL.revokeObjectURL(pdfUrlICI); setPdfUrlICI(null); } };

  const cargarMed = useCallback(async (cod, nombre) => {
    setModal(true); setMedSel({ codigo:cod, nombre }); setDataMed([]); setLoadModal(true);
    try {
      const r = await api.get('/farmacia/indicadores/medicamento-anual',{ params:{ idEstablecimiento:ipressActual?.id_establecimiento, codigoMed:cod } });
      if(r.data.success) setDataMed(r.data.data);
    } catch(e) { console.error(e); } finally { setLoadModal(false); }
  }, [ipressActual]);

  const cargarAuditoria = async () => {
    setModalAuditoria(true); setAuditTab(0); setLoadAuditoria(true); setAuditConsultado(false);
    try {
      const idEstab = ipressActual?.id_establecimiento || '';
      const [rA, rF] = await Promise.all([
        api.get('/farmacia/indicadores/auditoria-ici', { params:{ idEstablecimiento:idEstab, idUE:ueId } }),
        api.get('/farmacia/indicadores/auditoria-ici-faltantes', { params:{ periodo, idEstablecimiento:idEstab, idUE:ueId } }),
      ]);
      const anomalias = rA.data.success ? rA.data.data : [];
      const faltantes = rF.data.success ? rF.data.data : [];
      setAuditAnomalias(anomalias);
      setAuditFaltantes(faltantes);
      const distintos = new Set(anomalias.map(a=>a.idEstablecimiento)).size;
      setAuditEsUE(distintos>1 || faltantes.length>1);
    } catch(e) { console.error(e); }
    finally { setLoadAuditoria(false); setAuditConsultado(true); }
  };

  const totalHallazgos = auditFaltantes.length + auditAnomalias.length;

  const R = makeR(resumen);
  const dispVal = Number(R('disp'));
  const dCfg = dispCfg(dispVal);
  const mLabels = mesLabels(periodo);
  const tablaFPag = tablaF.slice((pgICI-1)*rppICI, pgICI*rppICI);
  const ultimoDisp = disponibilidad[disponibilidad.length-1]?.valor||0;

  const KPI_ICONS = {
  sobreStock: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M12 19V5M5 12l7-7 7 7"/></svg>,
  normoStock: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>,
  subStock: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M12 5v14M5 12l7 7 7-7"/></svg>,
  desabastecido: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  sinRotacion: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  sinConsumo: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>,
};

const kpis = [
    { campo:'sobreStock', porc:'porcSobre', label:'Sobrestock',    color:'#4DAE82', icon:KPI_ICONS.sobreStock },
    { campo:'normoStock', porc:'porcNormo', label:'Normostock',    color:'#83BF98', icon:KPI_ICONS.normoStock },
    { campo:'subStock',   porc:'porcSub',   label:'Substock',      color:'#E6A35D', icon:KPI_ICONS.subStock },
    { campo:'desabastecido',porc:'porcDesab',label:'Desabastecido',color:'#DE777E', icon:KPI_ICONS.desabastecido },
    { campo:'sinRotacion',porc:'porcRot',   label:'Sin Rotación',  color:'#A6B1C2', icon:KPI_ICONS.sinRotacion, ref:true },
    { campo:'sinConsumo', porc:'porcConsumo',label:'Sin Consumo',  color:'#8290A6', icon:KPI_ICONS.sinConsumo, ref:true },
  ];

  return (
    <>
      <style>{`.leaflet-pane{z-index:1!important}.leaflet-top,.leaflet-bottom{z-index:2!important}
        .leaflet-tooltip{background:white!important;border:1.5px solid ${IOS.blue}!important;border-radius:8px!important;padding:3px 9px!important;font-size:11px!important;font-weight:700!important;color:${IOS.blue}!important;}
        .leaflet-tooltip-top:before{border-top-color:${IOS.blue}!important;}
`}
      </style>

      <Box className="indicadores-page pharmacy-page" sx={{ minHeight:'100vh', bgcolor:IOS.bg }}>
        <HeaderIOS titulo="Indicadores" subtitulo="Farmacia"
          onHome={()=>navigate('/farmacia')} onLogout={handleLogout}
          nombreCompleto={user?.nombreCompleto||user?.usuario||''}
          avatarLetra={(user?.nombreCompleto||user?.usuario||'U').charAt(0).toUpperCase()} />

        {/* Tabs */}
        <Box className="pharmacy-tabs-row"><PharmacyTabs value={tab} onChange={setTab} options={['Dashboard','Detalles','Panorama']}/></Box>

        <Box className="pharmacy-content" sx={{ maxWidth:1600, mx:'auto', px:{ xs:1.5, sm:2.6 }, pt:2.5, pb:6 }}>

          <Box className="indicator-heading"><span className="indicator-eyebrow">Indicadores · Farmacia</span><Typography component="h1">{['El abastecimiento, en perspectiva','El detalle de tu disponibilidad','Tu red, de un vistazo'][tab]}</Typography><Typography>{['Revisa la disponibilidad y prioriza los medicamentos que necesitan atención.','Consulta los indicadores y la evolución de cada medicamento.','Compara establecimientos e identifica las prioridades de tu ejecutora.'][tab]}</Typography></Box>
          {/* ── Filtros comunes ── */}
          <Box className="pharmacy-layout pharmacy-toolbar pharmacy-card" sx={{ bgcolor:IOS.surface, borderRadius:'16px', p:2,
            boxShadow:'0 1px 4px rgba(0,0,0,0.07)', border:`1px solid ${IOS.separator}`,
            mb:2.5, display:'flex', gap:1.5, flexWrap:'wrap', alignItems:'flex-end' }}>
            <Sel label="IPRESS" value={ipressSel||''} disabled={loadingIP||!esTOTAL}
              onChange={e=>{ setIpressSel(e.target.value); const ip=ipressList.find(i=>i.nombre===e.target.value); if(ip?.ue_latitud) setMapCenter([parseFloat(ip.ue_latitud),parseFloat(ip.ue_longitud)]); }} w={{ xs:'100%', sm:260 }}>
              {ipressList.map((ip,i)=><option key={i} value={ip.nombre}>{ip.nombre}</option>)}
            </Sel>
            <Sel label="Período" value={periodo} onChange={e=>setPeriodo(e.target.value)} w={{ xs:'calc(50% - 8px)', sm:130 }}>
              {periodos().map(p=><option key={p} value={p}>{fmtP(p)}</option>)}
            </Sel>
            {tab===0 && (
              <Sel label="Top N" value={topN} onChange={e=>setTopN(Number(e.target.value))} w={{ xs:'calc(50% - 8px)', sm:110 }}>
                {[5,10,15,20].map(n=><option key={n} value={n}>Top {n}</option>)}
              </Sel>
            )}
            <Box className="pharmacy-layout" sx={{ ml:{ xs:0, sm:'auto' }, width:'auto', flex:{xs:'1 1 auto',sm:'0 0 auto'}, display:'flex', alignItems:'center', justifyContent:{ xs:'center', sm:'flex-start' }, gap:0.8,
              px:1.5, py:0.6, bgcolor:IOS.bg, borderRadius:'20px', border:`1px solid ${IOS.separator}` }}>
              <Box sx={{ width:7, height:7, borderRadius:'50%', bgcolor:IOS.green }} />
              <Typography sx={{ fontSize:'0.72rem', fontWeight:600, color:IOS.label2 }}>
                {ipressMarkers.length} IPRESS · {fmtP(periodo)}
              </Typography>
            </Box>
            <Tooltip title="Verificar períodos faltantes y anomalías en los datos" placement="top">
              <Box className="pharmacy-layout pharmacy-action" component="button" onClick={cargarAuditoria} disabled={loadAuditoria}
                sx={{ height:34, px:1.6, borderRadius:'10px', border:'1.5px solid #FFE9A0',
                  bgcolor:'#FFFDF0', cursor:loadAuditoria?'wait':'pointer', display:'flex',
                  alignItems:'center', gap:0.8, position:'relative', transition:'all 0.15s',
                  '&:hover':{ bgcolor:'#FFF8E6' } }}>
                {loadAuditoria
                  ? <CircularProgress size={13} sx={{ color:'#8B6914' }} />
                  : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#8B6914" strokeWidth="1.8" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/><path d="M11 8v3M11 14h.01"/></svg>}
                <Typography sx={{ fontSize:'0.78rem', fontWeight:700, color:'#8B6914' }}>Auditoría</Typography>
                {auditConsultado && totalHallazgos>0 && (
                  <Box className="pharmacy-layout" sx={{ minWidth:16, height:16, borderRadius:'20px', bgcolor:IOS.red,
                    display:'flex', alignItems:'center', justifyContent:'center', px:0.4 }}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight:800, color:'white' }}>{totalHallazgos}</Typography>
                  </Box>
                )}
              </Box>
            </Tooltip>
          </Box>

          {/* ══ TAB DASHBOARD ══════════════════════════════════════════════ */}
          {tab===0 && (
            <Box>
              <Box className="indicator-overview">
                <PharmacyMetric label="Disponibilidad actual" value={loadG?'…':disponibilidad.length?Number(ultimoDisp).toFixed(1)+'%':'—'} color={dispCfg(ultimoDisp).color} sub={disponibilidad.length?disponibilidad[disponibilidad.length-1].periodo:'Sin períodos disponibles'}/>
                <PharmacyMetric label="Cambio mensual" value={loadG?'…':disponibilidad.length>1?((ultimoDisp-disponibilidad[disponibilidad.length-2].valor)>=0?'+':'')+(ultimoDisp-disponibilidad[disponibilidad.length-2].valor).toFixed(1)+' pp':'—'} sub="Frente al período anterior"/>
                <PharmacyMetric label="Mejor disponibilidad" value={loadG?'…':disponibilidad.length?Math.max(...disponibilidad.map(d=>Number(d.valor))).toFixed(1)+'%':'—'} sub="En los períodos mostrados"/>
                <PharmacyMetric label="Establecimientos en mapa" value={loadingIP?'…':ipressMarkers.length} sub="IPRESS con ubicación registrada"/>
              </Box>
              {/* Mapa + Gráfico DME */}
              <Box className="pharmacy-layout indicator-hero">

                {/* Mapa */}
                <Box className="pharmacy-card indicator-map" sx={{ bgcolor:IOS.surface, borderRadius:'16px',
                  boxShadow:'0 1px 4px rgba(0,0,0,0.07)', border:`1px solid ${IOS.separator}`,
                  overflow:'hidden', height:{ xs:380, md:460 } }}>
                  <Box sx={{ px:2.5, py:1.8, bgcolor:`${IOS.blue}08`, borderBottom:`1px solid ${IOS.separator}` }}>
                    <Typography sx={{ fontWeight:700, fontSize:'0.88rem', color:IOS.label }}>
                      {ipressActual?.nombre||'Mapa de IPRESS'}
                    </Typography>
                    <Typography sx={{ fontSize:'0.7rem', color:IOS.label2, mt:0.2 }}>
                      {ipressMarkers.length} establecimientos
                    </Typography>
                  </Box>
                  {loadingIP
                    ? <Box className="pharmacy-layout" sx={{ height:{ xs:300, md:380 }, display:'flex', alignItems:'center', justifyContent:'center', bgcolor:IOS.bg }}><CircularProgress sx={{ color:IOS.blue }} /></Box>
                    : <Box className="indicator-map-body" sx={{ height:{ xs:300, md:380 } }}>
                        <MapContainer zoomAnimation={false} markerZoomAnimation={false} center={mapCenter} zoom={12} style={{ height:'100%', width:'100%' }} scrollWheelZoom={false}>
                          <OpenFreeMapLayer />
                          <MapCenter center={mapCenter} />
                          {ipressMarkers.map((ip,idx) => {
                            if(!ip.ue_latitud||!ip.ue_longitud) return null;
                            const sel = ip.nombre===ipressSel;
                            return (
                              <IpressMarker key={idx} ip={ip} selected={sel}
                                onClick={()=>{ setIpressSel(ip.nombre); setMapCenter([parseFloat(ip.ue_latitud),parseFloat(ip.ue_longitud)]); }} />
                            );
                          })}
                        </MapContainer>
                      </Box>
                  }
                  {/* Leyenda */}
                  <Box className="pharmacy-layout" sx={{ px:2, py:1, borderTop:`1px solid ${IOS.separator}`, display:'flex', gap:2, flexWrap:'wrap' }}>
                    {[['mapa_h.png','Hospital'],['mapa_cs.png','Centro Salud'],['mapa_ps.png','Puesto Salud']].map(([img,lbl])=>(
                      <Box className="pharmacy-layout" key={lbl} sx={{ display:'flex', alignItems:'center', gap:0.6 }}>
                        <img src={`/Imagenes/${img}`} alt={lbl} style={{ width:16, height:16 }} />
                        <Typography sx={{ fontSize: '0.72rem', color:IOS.label2 }}>{lbl}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* DME gráfico */}
                <ChartCard title="Disponibilidad ME Anual" sub="Evolución mensual del % DME — últimos 12 meses"
                  right={disponibilidad.length>0&&(
                    <Box sx={{ textAlign:'right' }}>
                      <Typography sx={{ fontSize:'2rem', fontWeight:800, color:dispCfg(ultimoDisp).color,
                        lineHeight:1, letterSpacing:'-0.03em' }}>{ultimoDisp.toFixed(1)}%</Typography>
                      <Typography sx={{ fontSize: '0.72rem', color:IOS.label2 }}>Último período</Typography>
                    </Box>
                  )}>
                  <Box sx={{ height:{ xs:260, md:340 } }}>
                    {loadG
                      ? <Box className="pharmacy-layout" sx={{ height:'100%', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:1 }}>
                          <CircularProgress sx={{ color:IOS.blue }} />
                          {esTodosUE && (
                            <Typography sx={{ fontSize:'0.72rem', color:IOS.label2, maxWidth:260, textAlign:'center' }}>
                              Calculando 12 meses de disponibilidad jurisdiccional — puede tardar
                            </Typography>
                          )}
                        </Box>
                      : disponibilidad.length>0
                        ? <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart accessibilityLayer={true} data={disponibilidad} margin={{ top:20, right:36, bottom:0, left:0 }}>
                              <defs>
                                <linearGradient id="gDisp" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%"  stopColor={IOS.blue} stopOpacity={0.12}/>
                                  <stop offset="95%" stopColor={IOS.blue} stopOpacity={0.01}/>
                                </linearGradient>
                              </defs>
                              <CartesianGrid horizontal={true} strokeDasharray="3 5" stroke="#EDF1F6" vertical={false}/>
                              <XAxis dataKey="periodo" fontSize={10} stroke={IOS.separator} tickLine={false} axisLine={false}/>
                              <YAxis domain={[0,100]} ticks={[0,25,50,75,100]} tickFormatter={v=>`${v}%`} fontSize={10} stroke={IOS.separator} tickLine={false} axisLine={false}/>
                              <ReferenceLine y={90} stroke="#4DAE82" strokeDasharray="5 4" strokeWidth={1.5} label={{ value:'90%', position:'insideTopRight', fontSize:9, fill:'#4DAE82', fontWeight:700 }}/>
                              <ReferenceLine y={80} stroke="#E6A35D" strokeDasharray="5 4" strokeWidth={1.2} label={{ value:'80%', position:'insideTopRight', fontSize:9, fill:'#E6A35D', fontWeight:700 }}/>
                              <ReferenceLine y={70} stroke="#DE777E" strokeDasharray="5 4" strokeWidth={1}   label={{ value:'70%', position:'insideTopRight', fontSize:9, fill:'#DE777E', fontWeight:700 }}/>
                              <RTooltip animationDuration={0} content={<TT/>} cursor={{ stroke:'transparent', fill:'transparent' }}/>
                              <Area isAnimationActive={!reducedMotion} animationEasing="ease-out" animationDuration={850} type="monotone" dataKey="valor" stroke="none" fill="url(#gDisp)" legendType="none" tooltipType="none"/>
                              <Line isAnimationActive={!reducedMotion} animationEasing="ease-out" animationDuration={850} type="monotone" dataKey="valor" stroke={IOS.blue} strokeWidth={3} name="% DME"
                                dot={({cx,cy,payload})=>{
                                  const c=dispCfg(payload.valor).color;
                                  return <g key={`d${cx}`}><circle cx={cx} cy={cy} r={9} fill={c} fillOpacity={0.12}/><circle cx={cx} cy={cy} r={4.5} fill={c} stroke="white" strokeWidth={2}/>{!compactCharts && <text x={cx} y={cy-14} textAnchor="middle" fontSize={8.5} fill={c} fontWeight={700}>{payload.valor.toFixed(0)}%</text>}</g>;
                                }}
                                activeDot={{ r:6, fill:IOS.blue, stroke:'white', strokeWidth:2 }}/>
                            </ComposedChart>
                          </ResponsiveContainer>
                        : <Box className="pharmacy-layout" sx={{ height:'100%', display:'flex', alignItems:'center', justifyContent:'center' }}>
                            <Typography sx={{ color:IOS.label2, fontSize:'0.85rem' }}>Sin datos</Typography>
                          </Box>
                    }
                  </Box>
                  {/* Leyenda */}
                  <Box className="pharmacy-layout" sx={{ display:'flex', gap:2, justifyContent:'center', mt:1.5, flexWrap:'wrap' }}>
                    {[['#4DAE82','Óptimo ≥90%'],['#83BF98','Bueno 80–89%'],['#E6A35D','Regular 70–79%'],['#DE777E','Crítico <70%']].map(([c,l])=>(
                      <Box className="pharmacy-layout" key={l} sx={{ display:'flex', alignItems:'center', gap:0.6 }}>
                        <Box sx={{ width:20, height:3, borderRadius:2, bgcolor:c }} />
                        <Typography sx={{ fontSize: '0.72rem', color:IOS.label2 }}>{l}</Typography>
                      </Box>
                    ))}
                  </Box>
                </ChartCard>
              </Box>

              {/* Top desabastecidos + Top substock */}
              <Box className="pharmacy-layout" sx={{ display:'grid', gridTemplateColumns:{ xs:'1fr', md:'1fr 1fr' }, gap:2 }}>
                {/* Desabastecidos */}
                <Box className="pharmacy-card" sx={{ bgcolor:IOS.surface, borderRadius:'16px', p:2.5,
                  boxShadow:'0 1px 4px rgba(0,0,0,0.07)', border:`1px solid ${IOS.separator}` }}>
                  <Box className="pharmacy-layout" sx={{ display:'flex', alignItems:'center', gap:1, mb:2 }}>
                    <Box sx={{ width:4, height:20, borderRadius:2, bgcolor:IOS.red }} />
                    <Box>
                      <Typography sx={{ fontWeight:700, fontSize:'0.9rem', color:IOS.label }}>Top {topN} ME Desabastecidos</Typography>
                      <Typography sx={{ fontSize: '0.72rem', color:IOS.label2 }}>
                        {esTodosUE ? 'Cantidad de EE.SS en esta condición' : 'CPMA — Consumo Promedio Mensual Ajustado'}
                      </Typography>
                    </Box>
                  </Box>
                  {loadG
                    ? <Box sx={{ py:8, textAlign:'center' }}><CircularProgress sx={{ color:IOS.blue }} /></Box>
                    : desabastecidos.length>0
                      ? <IndicatorRanking data={desabastecidos} dataKey="cpma" color={IOS.red} unit={esTodosUE?"EE.SS":"CPMA"}/>
                      : <Box sx={{ py:8, textAlign:'center' }}><Typography sx={{ color:IOS.label2, fontSize:'0.85rem' }}>Sin datos</Typography></Box>
                  }
                </Box>

                {/* Substock */}
                <Box className="pharmacy-card" sx={{ bgcolor:IOS.surface, borderRadius:'16px', p:2.5,
                  boxShadow:'0 1px 4px rgba(0,0,0,0.07)', border:`1px solid ${IOS.separator}` }}>
                  <Box className="pharmacy-layout" sx={{ display:'flex', alignItems:'center', gap:1, mb:2 }}>
                    <Box sx={{ width:4, height:20, borderRadius:2, bgcolor:IOS.orange }} />
                    <Box>
                      <Typography sx={{ fontWeight:700, fontSize:'0.9rem', color:IOS.label }}>Top {topN} ME Substock/Riesgo</Typography>
                      <Typography sx={{ fontSize: '0.72rem', color:IOS.label2 }}>
                        {esTodosUE ? 'Cantidad de EE.SS en esta condición' : 'MSD — Meses de Stock Disponible'}
                      </Typography>
                    </Box>
                  </Box>
                  {loadG
                    ? <Box sx={{ py:8, textAlign:'center' }}><CircularProgress sx={{ color:IOS.blue }} /></Box>
                    : substockTop.length>0
                      ? <IndicatorRanking data={substockTop} dataKey="msd" color={IOS.orange} unit={esTodosUE?"EE.SS":"Meses disponibles"}/>
                      : <Box sx={{ py:8, textAlign:'center' }}><Typography sx={{ color:IOS.label2, fontSize:'0.85rem' }}>Sin datos</Typography></Box>
                  }
                </Box>
              </Box>
            </Box>
          )}

          {/* ══ TAB DETALLES ═══════════════════════════════════════════════ */}
          {tab===1 && (
            loadRes
              ? <Box sx={{ py:10, textAlign:'center' }}>
                  <CircularProgress sx={{ color:IOS.blue, mb:2 }} />
                  <Typography sx={{ fontWeight:700, fontSize:'0.9rem', color:IOS.label, mb:0.5 }}>
                    {(esTodosUE||esHospitalFarmacias) ? 'Procesando toda la jurisdicción…' : 'Consultando…'}
                  </Typography>
                  {(esTodosUE||esHospitalFarmacias) && (
                    <Typography sx={{ color:IOS.label2, fontSize:'0.8rem', maxWidth:340, mx:'auto' }}>
                      Estamos recorriendo cada establecimiento — con tantos registros puede tardar unos segundos, espera por favor
                    </Typography>
                  )}
                </Box>
              : !resumen
                ? <Box className="pharmacy-card" sx={{ bgcolor:IOS.surface, borderRadius:'16px', py:8, textAlign:'center', border:`1px solid ${IOS.separator}` }}>
                    <Typography sx={{ color:IOS.label2, fontSize:'0.88rem' }}>
                      {ipressSel&&periodo?'Sin datos para este período':'Selecciona una IPRESS y período'}
                    </Typography>
                  </Box>
                : <Box>
                    {/* DME card grande + KPIs grid */}
                    <Box className="pharmacy-layout indicator-details-summary">
                      {/* DME */}
                      <Box className="pharmacy-card indicator-dme-summary" sx={{ bgcolor:IOS.surface, borderRadius:'16px', p:2.5,
                        boxShadow:'0 1px 4px rgba(0,0,0,0.07)', border:`1px solid ${IOS.separator}` }}>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:IOS.label2,
                          letterSpacing:'0.07em', textTransform:'uppercase', mb:1 }}>DME Período</Typography>
                        <Box className="pharmacy-layout" sx={{ display:'flex', alignItems:'baseline', gap:0.5, mb:1 }}>
                          <Typography sx={{ fontSize:'2.75rem', fontWeight:650, color:IOS.label,
                            lineHeight:1, letterSpacing:'-0.04em' }}>{dispVal.toFixed(1)}</Typography>
                          <Typography sx={{ fontSize:'1.3rem', fontWeight:600, color:IOS.label2, opacity:0.6 }}>%</Typography>
                        </Box>
                        <Box sx={{ height:5, bgcolor:IOS.separator, borderRadius:'20px', mb:1.2, overflow:'hidden' }}>
                          <Box sx={{ width:`${Math.min(dispVal,100)}%`, height:'100%', bgcolor:dCfg.color, borderRadius:'20px' }} />
                        </Box>
                        <Box sx={{ display:'inline-flex', alignItems:'center', gap:0.6,
                          px:1, py:0.3, borderRadius:'20px', bgcolor:`${dCfg.color}15`,
                          border:`1px solid ${dCfg.color}30`, mb:1 }}>
                          <Box sx={{ width:6, height:6, borderRadius:'50%', bgcolor:dCfg.color }} />
                          <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:dCfg.color }}>{dCfg.label}</Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.72rem', color:IOS.label2, display:'block' }}>
                          {R('totalME')} ME · {R('totalProductos')} productos
                        </Typography>
                      <IndicatorDonut data={kpis.filter(k=>!k.ref).map(k=>({name:k.label,value:R(k.campo),color:k.color}))}/>
</Box>

                      {/* KPIs */}
                      <Box className="pharmacy-layout" sx={{ display:'flex', flexDirection:'column', gap:1.5 }}>
                        <Box sx={{ display:'inline-flex', alignSelf:'flex-start', alignItems:'center', gap:0.6, px:1, py:0.4, borderRadius:'8px', bgcolor:IOS.bg, border:`1px solid ${IOS.separator}` }}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={IOS.label2} strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                          <Typography sx={{ fontSize: '0.72rem', color:IOS.label2, fontWeight:600 }}>
                            DIGEMID · {esTodosUE ? 'Ficha FT-UFAM-002 — Ámbito Jurisdiccional' : 'Ficha FT-EAM-001 — Por EE.SS'}
                          </Typography>
                        </Box>
                        <Box className="pharmacy-layout" sx={{ display:'grid', gridTemplateColumns:{ xs:'1fr 1fr', sm:'repeat(3,1fr)' }, gap:1.5 }}>
                          {kpis.map(k=>(
                            <KpiStrip key={k.campo} label={k.label} valor={R(k.campo)} porc={R(k.porc)} color={k.color} icon={k.icon}
                              esRef={k.ref} esJurisdiccion={esTodosUE} />
                          ))}
                        </Box>
                      </Box>
                    </Box>

                    {/* Tabla ICI */}
                    <Box className="pharmacy-card" sx={{ bgcolor:IOS.surface, borderRadius:'16px',
                      boxShadow:'0 1px 4px rgba(0,0,0,0.07)', border:`1px solid ${IOS.separator}`, overflow:'hidden' }}>

                      {/* Header tabla */}
                      <Box sx={{ p:2.5, borderBottom:`1px solid ${IOS.separator}` }}>
                        <Box className="pharmacy-layout" sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', mb:1.5, flexWrap:'wrap', gap:1 }}>
                          <Box>
                            <Typography sx={{ fontWeight:700, fontSize:'0.9rem', color:IOS.label }}>Detalle Completo ICI</Typography>
                            <Typography sx={{ fontSize:'0.7rem', color:IOS.label2, mt:0.2 }}>
                              {tablaF.length} de {tabla.length} {vistaTipo==='ME'?'medicamentos':'ítems'} · clic en fila para ver consumo mensual
                            </Typography>
                          </Box>
                          <Box className="pharmacy-layout" sx={{ display:'flex', alignItems:'center', gap:1 }}>
                            <Box className="pharmacy-layout" sx={{ display:'flex', gap:0.5, bgcolor:IOS.bg, borderRadius:'10px', p:0.4 }}>
                              {[['ME','Med. Esenciales'],['C','Catálogo Completo']].map(([val,lbl])=>(
                                <Box key={val} onClick={()=>setVistaTipo(val)}
                                  sx={{ px:1.5, py:0.6, borderRadius:'8px', cursor:'pointer',
                                    bgcolor:vistaTipo===val?IOS.surface:'transparent',
                                    boxShadow:vistaTipo===val?'0 1px 3px rgba(0,0,0,0.1)':'none', transition:'all 0.2s' }}>
                                  <Typography sx={{ fontSize:'0.72rem', fontWeight:vistaTipo===val?700:500,
                                    color:vistaTipo===val?IOS.label:IOS.label2, whiteSpace:'nowrap' }}>{lbl}</Typography>
                                </Box>
                              ))}
                            </Box>
                            <Tooltip title="Exportar informe" placement="top">
                              <Box className="pharmacy-layout pharmacy-action" aria-label="Descargar reporte" component="button" onClick={()=>setModalExpICI(true)}
                                sx={{ width:34, height:34, borderRadius:'10px', border:'none',
                                  bgcolor:IOS.green, cursor:'pointer',
                                  display:'flex', alignItems:'center', justifyContent:'center', transition:'all 0.15s',
                                  '&:hover':{ filter:'brightness(0.9)' } }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round"><path d="M12 3v13M7 11l5 5 5-5"/><path d="M4 20h16"/></svg>
                              </Box>
                            </Tooltip>
                          </Box>
                        </Box>
                        {vistaTipo==='C' && (
                          <Box className="pharmacy-layout" sx={{ mb:1.5, px:1.2, py:0.8, borderRadius:'10px', bgcolor:'#FFF8E6', border:'1px solid #FFE9A0',
                            display:'flex', alignItems:'center', gap:0.8 }}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#8B6914" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                            <Typography sx={{ fontSize:'0.72rem', color:'#8B6914', fontWeight:600 }}>
                              Catálogo completo (incluye insumos y dispositivos) — el % DME y los KPIs de arriba solo se calculan con Medicamentos Esenciales
                            </Typography>
                          </Box>
                        )}
                        {/* Pills filtro indicador */}
                        <Box className="pharmacy-layout" sx={{ display:'flex', gap:0.8, flexWrap:'wrap', mb:1.5 }}>
                          {['TODOS',...Object.keys(IND_CFG)].map(key=>{
                            const cfg = key==='TODOS'?null:IND_CFG[key];
                            const active = filtroInd===key;
                            return (
                              <Box key={key} onClick={()=>setFiltroInd(key)}
                                sx={{ px:1.2, py:0.3, borderRadius:'20px', cursor:'pointer',
                                  border:`1px solid ${active?(cfg?.border||IOS.blue):IOS.separator}`,
                                  bgcolor:active?(cfg?.bg||`${IOS.blue}12`):'transparent',
                                  transition:'all 0.15s' }}>
                                <Typography sx={{ fontSize: '0.72rem', fontWeight:700,
                                  color:active?(cfg?.text||IOS.blue):IOS.label2, whiteSpace:'nowrap' }}>
                                  {key==='TODOS'?'Todos':key.replace('SIN ROTACION','S. Rot.').replace('SIN CONSUMO','S. Cons.')}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                        {/* Búsqueda */}
                        <Box sx={{ position:'relative', maxWidth:{ xs:'100%', sm:400 } }}>
                          <Box sx={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:IOS.label2, pointerEvents:'none' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                          </Box>
                          <input type="text" placeholder="Buscar por código o producto..." value={busqueda}
                            onChange={e=>setBusqueda(e.target.value)}
                            style={{ width:'100%', height:34, paddingLeft:30, paddingRight:12,
                              border:`1px solid ${IOS.separator}`, borderRadius:8, fontSize:'0.78rem',
                              color:IOS.label, backgroundColor:IOS.surface, outline:'none',
                              boxSizing:'border-box', fontFamily:'inherit' }}
                            onFocus={e=>e.target.style.borderColor=IOS.blue}
                            onBlur={e=>e.target.style.borderColor=IOS.separator} />
                        </Box>
                      </Box>

                      {!esTodosUE && (<>
                      {/* Móvil — cards */}
                      <Box sx={{ display:{ xs:'block', md:'none' }, maxHeight:580, overflowY:'auto',
                        '&::-webkit-scrollbar':{ width:4 }, '&::-webkit-scrollbar-thumb':{ bgcolor:IOS.separator, borderRadius:4 } }}>
                        {tablaFPag.length===0
                          ? <Box sx={{ p:4, textAlign:'center' }}><Typography sx={{ color:IOS.label2, fontSize:'0.82rem' }}>Sin medicamentos</Typography></Box>
                          : tablaFPag.map((row,idx)=>{
                              const ind = T(row,'ind');
                              const msd = Number(T(row,'msd')||0);
                              const exp = filaExp===idx;
                              const meses = [1,2,3,4,5,6,7,8,9,10,11,12].map(n=>getMes(row,n));
                              const maxM = Math.max(...meses,1);
                              return (
                                <Box key={idx} sx={{ borderBottom:`1px solid ${IOS.separator}` }}>
                                  <Box onClick={()=>setFilaExp(exp?null:idx)} sx={{ p:2, cursor:'pointer', bgcolor:exp?`${IOS.blue}06`:IOS.surface }}>
                                    <Box className="pharmacy-layout" sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:1, mb:0.8 }}>
                                      <Box sx={{ minWidth:0 }}>
                                        <Typography sx={{ fontWeight:700, fontSize:'0.8rem', color:IOS.label, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{T(row,'producto')}</Typography>
                                        <Typography sx={{ fontSize: '0.72rem', color:IOS.label2, fontFamily:'monospace' }}>{T(row,'codigo')}</Typography>
                                      </Box>
                                      <IndPill valor={ind} sm />
                                    </Box>
                                    <Box className="pharmacy-layout" sx={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:0.8, mb:0.8 }}>
                                      {[['Consumo',T(row,'consumo')||0],['Stock',Number(T(row,'stock')||0).toFixed(0)],['CPMA',Number(T(row,'cpma')||0).toFixed(2)],['MSD',msd.toFixed(2)]].map(([l,v],j)=>(
                                        <Box key={j} sx={{ bgcolor:IOS.bg, borderRadius:'8px', px:0.5, py:0.5, textAlign:'center' }}>
                                          <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:IOS.label2, textTransform:'uppercase' }}>{l}</Typography>
                                          <Typography sx={{ fontSize:'0.74rem', fontWeight:700, color:IOS.label }}>{v}</Typography>
                                        </Box>
                                      ))}
                                    </Box>
                                    <Box className="pharmacy-layout" sx={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                                      <MiniSparkline meses={meses} />
                                      <Box component="button" type="button" aria-label="Ver evolución del medicamento" onClick={e=>{ e.stopPropagation(); cargarMed(T(row,'codigo'),T(row,'producto')); }}
                                        sx={{ width:30, height:30, borderRadius:'50%', bgcolor:`${IOS.blue}12`, display:'inline-flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                                      </Box>
                                    </Box>
                                  </Box>
                                  {exp && (
                                    <Box sx={{ px:2, py:2, bgcolor:IOS.bg }}>
                                      <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:IOS.label2, mb:1.2, textTransform:'uppercase', letterSpacing:'0.06em' }}>
                                        Consumo mensual
                                      </Typography>
                                      <Box className="pharmacy-layout" sx={{ display:'flex', gap:0.8, alignItems:'flex-end', overflowX:'auto', pb:0.5 }}>
                                        {meses.map((val,mi)=>{
                                          const pct=maxM>0?(val/maxM)*100:0;
                                          const ml=mLabels[mi]; const cur=ml?.isCurrent;
                                          return (
                                            <Box className="pharmacy-layout" key={mi} sx={{ display:'flex', flexDirection:'column', alignItems:'center', flex:1, minWidth:30 }}>
                                              <Typography sx={{ fontSize: '0.72rem', fontWeight:600, color:val===0?IOS.label3:IOS.label, mb:0.4 }}>{val}</Typography>
                                              <Box className="pharmacy-layout" sx={{ width:'70%', height:36, display:'flex', alignItems:'flex-end', justifyContent:'center' }}>
                                                <Box sx={{ width:'100%', minHeight:val===0?0:3, height:`${Math.max(pct,val===0?0:5)}%`, bgcolor:val===0?IOS.separator:cur?IOS.blue:`${IOS.blue}60`, borderRadius:'4px 4px 0 0' }} />
                                              </Box>
                                              <Typography sx={{ fontSize: '0.72rem', color:IOS.label2, mt:0.3, whiteSpace:'nowrap', fontWeight:cur?700:400 }}>{ml?.short||`M${mi+1}`}</Typography>
                                            </Box>
                                          );
                                        })}
                                      </Box>
                                    </Box>
                                  )}
                                </Box>
                              );
                          })
                        }
                      </Box>
                      {/* Desktop — tabla */}
                      <Box className="pharmacy-scroll" tabIndex={0} role="region" aria-label="Tabla de resultados, desplazamiento horizontal" sx={{ display:{ xs:'none', md:'block' }, overflowX:'auto', maxHeight:580,
                        '&::-webkit-scrollbar':{ width:4, height:4 },
                        '&::-webkit-scrollbar-thumb':{ bgcolor:IOS.separator, borderRadius:4 } }}>
                        <table style={{ width:'100%', minWidth:680, borderCollapse:'collapse', fontSize:'0.75rem' }}>
                          <thead style={{ position:'sticky', top:0, zIndex:2 }}>
                            <tr>
                              <th style={{ width:32, backgroundColor:IOS.surface, borderBottom:`1px solid ${IOS.separator}`, position:'sticky', top:0 }} />
                              {['Código','Producto','Consumo','Stock','CPMA','MSD','Tend. 12M','Indicador',''].map((h,i)=>(
                                <th key={i} style={{ padding:'9px 10px',
                                  textAlign:i>=2&&i<=5?'right':i>=6?'center':'left',
                                  fontSize: '0.72rem', fontWeight:700, color:IOS.label2,
                                  letterSpacing:'0.06em', textTransform:'uppercase',
                                  backgroundColor:IOS.surface, borderBottom:`1px solid ${IOS.separator}`,
                                  whiteSpace:'nowrap' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {tablaFPag.length===0
                              ? <tr><td colSpan={9} style={{ padding:'40px', textAlign:'center', color:IOS.label2 }}>Sin medicamentos</td></tr>
                              : tablaFPag.map((row,idx)=>(
                                  <FilaICI key={idx} row={row} idx={idx} exp={filaExp===idx} mLabels={mLabels} onToggle={toggleFila} onVerMed={cargarMed} />
                                ))
                            }
                          </tbody>
                        </table>
                      </Box>
                      </>)}

                      {/* Tabla jurisdiccional (rollup por EE.SS) — solo para "Todos" */}
                      {esTodosUE && (() => {
                        const N = (v) => v>0 ? v : '';
                        const tot = {
                          desab: tablaF.reduce((s,r)=>s+Number(r.desabastecido||0),0),
                          sub:   tablaF.reduce((s,r)=>s+Number(r.substock||0),0),
                          normo: tablaF.reduce((s,r)=>s+Number(r.normostock||0),0),
                          sobre: tablaF.reduce((s,r)=>s+Number(r.sobrestock||0),0),
                          sinC:  tablaF.reduce((s,r)=>s+Number(r.sinConsumo||0),0),
                          sinR:  tablaF.reduce((s,r)=>s+Number(r.sinRotacion||0),0),
                          total: tablaF.reduce((s,r)=>s+Number(r.total||0),0),
                        };
                        return (
                        <Box>
                        {/* Móvil — cards */}
                        <Box sx={{ display:{ xs:'block', md:'none' }, maxHeight:580, overflowY:'auto',
                          '&::-webkit-scrollbar':{ width:4 }, '&::-webkit-scrollbar-thumb':{ bgcolor:IOS.separator, borderRadius:4 } }}>
                          {tablaFPag.length===0
                            ? <Box sx={{ p:4, textAlign:'center' }}><Typography sx={{ color:IOS.label2, fontSize:'0.82rem' }}>Sin medicamentos</Typography></Box>
                            : tablaFPag.map((row,idx)=>(
                                <Box key={idx} sx={{ p:2, borderBottom:`1px solid ${IOS.separator}` }}>
                                  <Box className="pharmacy-layout" sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:1, mb:0.8 }}>
                                    <Box sx={{ minWidth:0 }}>
                                      <Typography sx={{ fontWeight:700, fontSize:'0.8rem', color:'#1F2937', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{T(row,'producto')}</Typography>
                                      <Typography sx={{ fontSize: '0.72rem', color:IOS.label2, fontFamily:'monospace' }}>{T(row,'codigo')}</Typography>
                                    </Box>
                                    <Box sx={{ textAlign:'right', flexShrink:0 }}>
                                      <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:IOS.label2, textTransform:'uppercase' }}>Total</Typography>
                                      <Typography sx={{ fontSize:'0.9rem', fontWeight:800, color:IOS.label }}>{row.total}</Typography>
                                    </Box>
                                  </Box>
                                  <Box className="pharmacy-layout" sx={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:0.6 }}>
                                    {[['Desab.',row.desabastecido,IOS.red],['Substock',row.substock,IOS.orange],['Normost.',row.normostock,'#83BF98'],
                                      ['Sobrest.',row.sobrestock,'#4DAE82'],['Sin Cons.',row.sinConsumo,IOS.label2],['Sin Rot.',row.sinRotacion,IOS.label2]].map(([l,v,c],j)=>(
                                      <Box key={j} sx={{ bgcolor:IOS.bg, borderRadius:'8px', px:0.5, py:0.5, textAlign:'center' }}>
                                        <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:IOS.label2, textTransform:'uppercase' }}>{l}</Typography>
                                        <Typography sx={{ fontSize:'0.74rem', fontWeight:700, color:v>0?c:IOS.label3 }}>{v>0?v:'—'}</Typography>
                                      </Box>
                                    ))}
                                  </Box>
                                </Box>
                              ))
                          }
                        </Box>
                        {/* Desktop — tabla */}
                        <Box className="pharmacy-scroll" tabIndex={0} role="region" aria-label="Tabla de resultados, desplazamiento horizontal" sx={{ display:{ xs:'none', md:'block' }, overflowX:'auto', maxHeight:580,
                          '&::-webkit-scrollbar':{ width:4, height:4 }, '&::-webkit-scrollbar-thumb':{ bgcolor:IOS.separator, borderRadius:4 } }}>
                          <table style={{ width:'100%', minWidth:640, borderCollapse:'collapse', fontSize:'0.75rem' }}>
                            <thead style={{ position:'sticky', top:0, zIndex:2 }}>
                              <tr>
                                {['Código','Producto','Desab.','Substock','Normostock','Sobrestock','Sin Cons.','Sin Rot.','Total'].map((h,i)=>(
                                  <th key={i} style={{ padding:'9px 10px', textAlign:i>=2?'right':'left',
                                    fontSize: '0.72rem', fontWeight:700, color:IOS.label2, letterSpacing:'0.06em', textTransform:'uppercase',
                                    backgroundColor:IOS.surface, borderBottom:`1px solid ${IOS.separator}`, whiteSpace:'nowrap' }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {tablaFPag.length===0
                                ? <tr><td colSpan={9} style={{ padding:'40px', textAlign:'center', color:IOS.label2 }}>Sin medicamentos</td></tr>
                                : tablaFPag.map((row,idx)=>(
                                    <tr key={idx} style={{ borderBottom:`1px solid ${IOS.separator}`, backgroundColor:IOS.surface, transition:'background 0.1s' }}
                                      onMouseEnter={e=>e.currentTarget.style.backgroundColor=IOS.bg}
                                      onMouseLeave={e=>e.currentTarget.style.backgroundColor=IOS.surface}>
                                      <td style={{ padding:'8px 10px', fontSize:'0.72rem', color:IOS.label2, fontFamily:'monospace' }}>{T(row,'codigo')}</td>
                                      <td style={{ padding:'8px 10px', fontSize:'0.76rem', color:IOS.label, maxWidth:220 }}>
                                        <Tooltip title={T(row,'producto')} placement="top">
                                          <span style={{ display:'block', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:220 }}>{T(row,'producto')}</span>
                                        </Tooltip>
                                      </td>
                                      <td style={{ padding:'8px 10px', textAlign:'right', fontWeight:row.desabastecido>0?700:400, color:row.desabastecido>0?IOS.red:IOS.label3 }}>{N(row.desabastecido)}</td>
                                      <td style={{ padding:'8px 10px', textAlign:'right', fontWeight:row.substock>0?700:400, color:row.substock>0?IOS.orange:IOS.label3 }}>{N(row.substock)}</td>
                                      <td style={{ padding:'8px 10px', textAlign:'right', fontWeight:row.normostock>0?700:400, color:row.normostock>0?'#83BF98':IOS.label3 }}>{N(row.normostock)}</td>
                                      <td style={{ padding:'8px 10px', textAlign:'right', fontWeight:row.sobrestock>0?700:400, color:row.sobrestock>0?'#4DAE82':IOS.label3 }}>{N(row.sobrestock)}</td>
                                      <td style={{ padding:'8px 10px', textAlign:'right', color:IOS.label3 }}>{N(row.sinConsumo)}</td>
                                      <td style={{ padding:'8px 10px', textAlign:'right', color:IOS.label3 }}>{N(row.sinRotacion)}</td>
                                      <td style={{ padding:'8px 10px', textAlign:'right', fontWeight:800, color:IOS.label }}>{row.total}</td>
                                    </tr>
                                  ))
                              }
                            </tbody>
                            {tablaF.length>0 && (
                              <tfoot>
                                <tr>
                                  <td colSpan={2} style={{ padding:'9px 10px', fontSize: '0.72rem', fontWeight:700, color:IOS.label2, borderTop:`2px solid ${IOS.separator}`, backgroundColor:IOS.bg }}>
                                    Totales — {tablaF.length} medicamentos
                                  </td>
                                  <td style={{ padding:'9px 10px', textAlign:'right', fontWeight:800, color:IOS.red, borderTop:`2px solid ${IOS.separator}`, backgroundColor:IOS.bg }}>{tot.desab}</td>
                                  <td style={{ padding:'9px 10px', textAlign:'right', fontWeight:800, color:IOS.orange, borderTop:`2px solid ${IOS.separator}`, backgroundColor:IOS.bg }}>{tot.sub}</td>
                                  <td style={{ padding:'9px 10px', textAlign:'right', fontWeight:800, color:'#83BF98', borderTop:`2px solid ${IOS.separator}`, backgroundColor:IOS.bg }}>{tot.normo}</td>
                                  <td style={{ padding:'9px 10px', textAlign:'right', fontWeight:800, color:'#4DAE82', borderTop:`2px solid ${IOS.separator}`, backgroundColor:IOS.bg }}>{tot.sobre}</td>
                                  <td style={{ padding:'9px 10px', textAlign:'right', fontWeight:700, color:IOS.label2, borderTop:`2px solid ${IOS.separator}`, backgroundColor:IOS.bg }}>{tot.sinC}</td>
                                  <td style={{ padding:'9px 10px', textAlign:'right', fontWeight:700, color:IOS.label2, borderTop:`2px solid ${IOS.separator}`, backgroundColor:IOS.bg }}>{tot.sinR}</td>
                                  <td style={{ padding:'9px 10px', textAlign:'right', fontWeight:800, color:IOS.blue, fontSize:'0.85rem', borderTop:`2px solid ${IOS.separator}`, backgroundColor:IOS.bg }}>{tot.total}</td>
                                </tr>
                              </tfoot>
                            )}
                          </table>
                        </Box>
                        </Box>
                        );
                      })()}
                      <PaginacionICI total={tablaF.length} pagina={pgICI} setPagina={setPgICI} rpp={rppICI} setRpp={setRppICI} />
                    </Box>
                  </Box>
          )}

          {/* ══ TAB REPORTE GERENCIAL ══════════════════════════════════════ */}
          {/* ══ TAB REPORTE GERENCIAL ══════════════════════════════════════ */}
          {tab===2 && (() => {
            const rango = (nombre='') => {
              const u = nombre.toUpperCase();
              if (u.includes('HOSPITAL')||u.includes('FARMACIA')) return 0;
              if (u.includes('C.S.')||u.includes('CENTRO DE SALUD')) return 1;
              return 2;
            };
            const filasBase = esTodosUE
              ? gerencial
              : (resumen ? [{
                  idEstablecimiento: ipressActual?.id_establecimiento,
                  nombreEstablecimiento: ipressActual?.nombre || '',
                  esHospital: esHospitalFarmacias,
                  totalProductos: Number(R('totalProductos'))||0,
                  totalME: Number(R('totalME'))||0,
                  sobrestock: Number(R('sobreStock'))||0,
                  normostock: Number(R('normoStock'))||0,
                  substock: Number(R('subStock'))||0,
                  desabastecido: Number(R('desabastecido'))||0,
                  sinRotacion: Number(R('sinRotacion'))||0,
                  sinConsumo: Number(R('sinConsumo'))||0,
                  disp_ME_2meses: Number(R('disp'))||0,
                }] : []);
            const cargando = esTodosUE ? loadGer : loadRes;
            const filasJerarquico = [...filasBase].sort((a,b)=>{
              const rA=rango(a.nombreEstablecimiento), rB=rango(b.nombreEstablecimiento);
              return rA!==rB ? rA-rB : a.disp_ME_2meses-b.disp_ME_2meses;
            });
            const filasPorDisp = [...filasBase].sort((a,b)=>a.disp_ME_2meses-b.disp_ME_2meses);
            const filasTabla = busquedaGer.trim()
              ? filasJerarquico.filter(r=>(r.nombreEstablecimiento||'').toUpperCase().includes(busquedaGer.trim().toUpperCase()))
              : filasJerarquico;

            if (cargando) return (
              <Box sx={{ py:10, textAlign:'center' }}>
                <CircularProgress sx={{ color:IOS.blue, mb:2 }} />
                <Typography sx={{ fontWeight:700, fontSize:'0.9rem', color:IOS.label, mb:0.5 }}>
                  {esTodosUE ? 'Procesando comparativo jurisdiccional…' : 'Consultando…'}
                </Typography>
                {esTodosUE && (
                  <Typography sx={{ color:IOS.label2, fontSize:'0.8rem', maxWidth:340, mx:'auto' }}>
                    Estamos calculando el resumen de cada establecimiento — la primera vez puede tardar unos segundos, las siguientes son casi instantáneas
                  </Typography>
                )}
              </Box>
            );
            if (filasBase.length===0) return (
              <Box className="pharmacy-card" sx={{ bgcolor:IOS.surface, borderRadius:'16px', py:8, textAlign:'center', border:`1px solid ${IOS.separator}` }}>
                <Typography sx={{ color:IOS.label2, fontSize:'0.88rem' }}>Sin datos para este período</Typography>
              </Box>
            );

            const renderEvolucion = () => (
              <Box className="pharmacy-layout" sx={{ display:'grid', gridTemplateColumns:{ xs:'1fr', md:'1fr 1fr' }, gap:1.5 }}>
                <Box>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:IOS.label2, mb:0.8, textTransform:'uppercase', letterSpacing:'0.05em' }}>
                    Disponibilidad ME — 12 meses
                  </Typography>
                  {loadEvol
                    ? <Box className="pharmacy-layout" sx={{ height:230, display:'flex', alignItems:'center', justifyContent:'center' }}><CircularProgress size={20} sx={{ color:IOS.blue }}/></Box>
                    : <Box sx={{ height:230 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart accessibilityLayer={true} data={evolData} margin={{ top:14, right:16, left:0, bottom:0 }}>
                            <CartesianGrid horizontal={true} strokeDasharray="3 5" stroke="#EDF1F6" vertical={false}/>
                            <XAxis dataKey="periodo" tick={{ fill:'#374151', fontSize:9 }} stroke={IOS.separator} tickLine={false} axisLine={false}/>
                            <YAxis domain={[0,100]} tickFormatter={v=>`${v}%`} tick={{ fill:'#374151', fontSize:9 }} stroke={IOS.separator} tickLine={false} axisLine={false}/>
                            <RTooltip animationDuration={0} content={<TT/>}/><defs><linearGradient id="indicator-evolution" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={IOS.blue} stopOpacity={0.18}/><stop offset="100%" stopColor={IOS.blue} stopOpacity={0}/></linearGradient></defs><Area dataKey="disp" type="monotone" stroke="none" fill="url(#indicator-evolution)" tooltipType="none" legendType="none" isAnimationActive={!reducedMotion} animationDuration={850} animationEasing="ease-out"/>
                            <Line isAnimationActive={!reducedMotion} animationEasing="ease-out" animationDuration={850} type="monotone" dataKey="disp" name="% DME" stroke={IOS.blue} strokeWidth={3}
                              dot={({cx,cy,payload})=>{ const c=dispCfg(payload.disp).color; return <circle key={`e${cx}`} cx={cx} cy={cy} r={3.5} fill={c} stroke="white" strokeWidth={1.5}/>; }}/>
                          </ComposedChart>
                        </ResponsiveContainer>
                      </Box>
                  }
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:IOS.label2, mb:0.8, textTransform:'uppercase', letterSpacing:'0.05em' }}>
                    Composición mensual
                  </Typography>
                  {loadEvol
                    ? <Box className="pharmacy-layout" sx={{ height:230, display:'flex', alignItems:'center', justifyContent:'center' }}><CircularProgress size={20} sx={{ color:IOS.blue }}/></Box>
                    : <Box sx={{ height:230 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart accessibilityLayer={true} data={evolData} margin={{ top:14, right:16, left:0, bottom:0 }}>
                            <CartesianGrid horizontal={true} strokeDasharray="3 5" stroke="#EDF1F6" vertical={false}/>
                            <XAxis dataKey="periodo" tick={{ fill:'#374151', fontSize:9 }} stroke={IOS.separator} tickLine={false} axisLine={false}/>
                            <YAxis tick={{ fill:'#374151', fontSize:9 }} stroke={IOS.separator} tickLine={false} axisLine={false}/>
                            <RTooltip animationDuration={0} content={<TT/>}/>
                            <Bar shape={<IndicatorStackShape field="sobrestock" horizontal={false}/>} maxBarSize={26} isAnimationActive={!reducedMotion} animationEasing="ease-out" animationDuration={850} dataKey="sobrestock" stackId="a" name="Sobrestock" fill={IND_CFG.SOBRESTOCK.dot}/>
                            <Bar shape={<IndicatorStackShape field="normostock" horizontal={false}/>} maxBarSize={26} isAnimationActive={!reducedMotion} animationEasing="ease-out" animationDuration={850} dataKey="normostock" stackId="a" name="Normostock" fill={IND_CFG.NORMOSTOCK.dot}/>
                            <Bar shape={<IndicatorStackShape field="substock" horizontal={false}/>} maxBarSize={26} isAnimationActive={!reducedMotion} animationEasing="ease-out" animationDuration={850} dataKey="substock" stackId="a" name="Substock" fill={IND_CFG.SUBSTOCK.dot}/>
                            <Bar shape={<IndicatorStackShape field="desabastecido" horizontal={false}/>} maxBarSize={26} isAnimationActive={!reducedMotion} animationEasing="ease-out" animationDuration={850} dataKey="desabastecido" stackId="a" name="Desabastecido" fill={IND_CFG.DESABASTECIDO.dot} radius={[6,6,0,0]}/>
                          </ComposedChart>
                        </ResponsiveContainer>
                      </Box>
                  }
                </Box>
              </Box>
            );

            return (
              <Box>
                {/* Gráficos generales */}
                <Box className="pharmacy-layout" sx={{ display:'grid', gridTemplateColumns:{ xs:'1fr', md:'1fr 1fr' }, gap:2, mb:2 }}>
                  <ChartCard title="Composición ME por Establecimiento" sub="Sobrestock · Normostock · Substock · Desabastecido">
                    <Box sx={{ height:Math.max(220, filasJerarquico.length*32), overflowX:'auto' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart accessibilityLayer={true} data={filasJerarquico} layout="vertical" margin={{ top:4, right:20, left:4, bottom:4 }}>
                          <CartesianGrid vertical={false} strokeDasharray="3 5" stroke="#EDF1F6" horizontal={true}/>
                          <XAxis type="number" tick={{ fill:'#374151', fontSize:10 }} stroke={IOS.separator} tickLine={false} axisLine={false}/>
                          <YAxis type="category" dataKey="nombreEstablecimiento" width={compactCharts?110:150} tickFormatter={v=>v.length>(compactCharts?15:23)?v.slice(0,compactCharts?15:23)+"…":v} tick={{ fill:'#374151', fontSize:9 }} stroke={IOS.separator} tickLine={false} axisLine={false}/>
                          <RTooltip animationDuration={0} content={<TT/>}/>
                          <Bar shape={<IndicatorStackShape field="sobrestock" horizontal={true}/>} maxBarSize={26} isAnimationActive={!reducedMotion} animationEasing="ease-out" animationDuration={850} dataKey="sobrestock" stackId="a" name="Sobrestock" fill={IND_CFG.SOBRESTOCK.dot}/>
                          <Bar shape={<IndicatorStackShape field="normostock" horizontal={true}/>} maxBarSize={26} isAnimationActive={!reducedMotion} animationEasing="ease-out" animationDuration={850} dataKey="normostock" stackId="a" name="Normostock" fill={IND_CFG.NORMOSTOCK.dot}/>
                          <Bar shape={<IndicatorStackShape field="substock" horizontal={true}/>} maxBarSize={26} isAnimationActive={!reducedMotion} animationEasing="ease-out" animationDuration={850} dataKey="substock" stackId="a" name="Substock" fill={IND_CFG.SUBSTOCK.dot}/>
                          <Bar shape={<IndicatorStackShape field="desabastecido" horizontal={true}/>} maxBarSize={26} isAnimationActive={!reducedMotion} animationEasing="ease-out" animationDuration={850} dataKey="desabastecido" stackId="a" name="Desabastecido" fill={IND_CFG.DESABASTECIDO.dot} radius={[0,6,6,0]}/>
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Box>
                  </ChartCard>

                  <ChartCard title="Ranking de Disponibilidad ME" sub="Menor a mayor — dónde atacar primero">
                    <Box sx={{ height:Math.max(220, filasPorDisp.length*32), overflowX:'auto' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart accessibilityLayer={true} data={filasPorDisp} layout="vertical" margin={{ top:4, right:36, left:4, bottom:4 }}>
                          <CartesianGrid vertical={false} strokeDasharray="3 5" stroke="#EDF1F6" horizontal={true}/>
                          <XAxis type="number" domain={[0,100]} tickFormatter={v=>`${v}%`} tick={{ fill:'#374151', fontSize:10 }} stroke={IOS.separator} tickLine={false} axisLine={false}/>
                          <YAxis type="category" dataKey="nombreEstablecimiento" width={compactCharts?110:150} tickFormatter={v=>v.length>(compactCharts?15:23)?v.slice(0,compactCharts?15:23)+"…":v} tick={{ fill:'#374151', fontSize:9 }} stroke={IOS.separator} tickLine={false} axisLine={false}/>
                          <RTooltip animationDuration={0} content={<TT/>}/>
                          <ReferenceLine x={80} stroke="#83BF98" strokeDasharray="4 4"/>
                          <ReferenceLine x={70} stroke="#DE777E" strokeDasharray="4 4"/>
                          <Bar maxBarSize={26} isAnimationActive={!reducedMotion} animationEasing="ease-out" animationDuration={850} dataKey="disp_ME_2meses" name="% Disponibilidad" radius={[0,6,6,0]}>
                            {filasPorDisp.map((f,i)=><Cell key={i} fill={dispCfg(f.disp_ME_2meses).color}/>)}
                          </Bar>
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Box>
                  </ChartCard>
                </Box>

                {/* Tabla con búsqueda + evolución inline al hacer clic */}
                <Box className="pharmacy-card" sx={{ bgcolor:IOS.surface, borderRadius:'16px', boxShadow:'0 1px 4px rgba(0,0,0,0.07)', border:`1px solid ${IOS.separator}`, overflow:'hidden' }}>
                  <Box sx={{ p:2.5, borderBottom:`1px solid ${IOS.separator}` }}>
                    <Typography sx={{ fontWeight:700, fontSize:'0.9rem', color:IOS.label }}>
                      {esTodosUE ? 'Comparativo por Establecimiento' : 'Reporte Gerencial'}
                    </Typography>
                    <Typography sx={{ fontSize:'0.7rem', color:IOS.label2, mt:0.2, mb:1.2 }}>
                      {esTodosUE
                        ? `${filasTabla.length} de ${filasBase.length} establecimientos · ${fmtP(periodo)} · toca una fila para ver su evolución`
                        : `${ipressActual?.nombre||''} · ${fmtP(periodo)}`}
                    </Typography>
                    {esTodosUE && (
                      <Box sx={{ position:'relative', maxWidth:{ xs:'100%', sm:340 } }}>
                        <Box sx={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:IOS.label2, pointerEvents:'none' }}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                        </Box>
                        <input type="text" placeholder="Buscar IPRESS..." value={busquedaGer}
                          onChange={e=>setBusquedaGer(e.target.value)}
                          style={{ width:'100%', height:32, paddingLeft:28, paddingRight:10,
                            border:`1px solid ${IOS.separator}`, borderRadius:8, fontSize:'0.76rem',
                            color:IOS.label, backgroundColor:IOS.surface, outline:'none', boxSizing:'border-box', fontFamily:'inherit' }}
                          onFocus={e=>e.target.style.borderColor=IOS.blue}
                          onBlur={e=>e.target.style.borderColor=IOS.separator} />
                      </Box>
                    )}
                  </Box>

                  {/* Móvil — cards */}
                  <Box sx={{ display:{ xs:'block', md:'none' } }}>
                    {filasTabla.map((r,i)=>{
                      const dc = dispCfg(r.disp_ME_2meses);
                      const sel = selEvol?.id===r.idEstablecimiento;
                      return (
                        <Box key={i} sx={{ borderBottom:`1px solid ${IOS.separator}` }}>
                          <Box onClick={()=>cargarEvolucionIpress(r.idEstablecimiento, r.nombreEstablecimiento, !!r.esHospital)}
                            sx={{ p:2, cursor:'pointer', bgcolor:sel?`${IOS.blue}08`:IOS.surface }}>
                            <Box className="pharmacy-layout" sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:1, mb:0.8 }}>
                              <Typography sx={{ fontWeight:700, fontSize:'0.8rem', color:'#1F2937', flex:1 }}>{r.nombreEstablecimiento}</Typography>
                              <Box sx={{ display:'inline-flex', px:1, py:0.3, borderRadius:'20px', bgcolor:`${dc.color}15`, border:`1px solid ${dc.color}40`, flexShrink:0 }}>
                                <Typography sx={{ fontSize:'0.76rem', fontWeight:800, color:dc.color }}>{r.disp_ME_2meses.toFixed(1)}%</Typography>
                              </Box>
                            </Box>
                            <Box className="pharmacy-layout" sx={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:0.6 }}>
                              {[['Sobre',r.sobrestock,IND_CFG.SOBRESTOCK],['Normo',r.normostock,IND_CFG.NORMOSTOCK],['Sub',r.substock,IND_CFG.SUBSTOCK],['Desab',r.desabastecido,IND_CFG.DESABASTECIDO]].map(([l,v,cfg],j)=>(
                                <Box key={j} sx={{ bgcolor:IOS.bg, borderRadius:'8px', px:0.5, py:0.5, textAlign:'center' }}>
                                  <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:IOS.label2, textTransform:'uppercase' }}>{l}</Typography>
                                  <Typography sx={{ fontSize:'0.74rem', fontWeight:700, color:cfg.text }}>{v}</Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                          {sel && (
                            <Box sx={{ px:2, py:2, bgcolor:IOS.bg }}>
                              {renderEvolucion()}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>

                  {/* Desktop — tabla */}
                  <Box sx={{ display:{ xs:'none', md:'block' }, overflowX:'auto' }}>
                    <table style={{ width:'100%', minWidth:1100, borderCollapse:'collapse', fontSize:'0.74rem' }}>
                      <thead>
                        <tr>
                          {['IPRESS','Total','ME','ME %','Sobrestock','Normostock','Substock','Desabastecido','Sin Rot.','Sin Cons.','Disp. ME >2m'].map((h,i)=>(
                            <th key={i} style={{ padding:'9px 10px', textAlign:i===0?'left':'center',
                              fontSize: '0.72rem', fontWeight:700, color:IOS.label2, letterSpacing:'0.05em', textTransform:'uppercase',
                              backgroundColor:IOS.surface, borderBottom:`1px solid ${IOS.separator}`, whiteSpace:'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filasTabla.map((r,i)=>{
                          const dc = dispCfg(r.disp_ME_2meses);
                          const Cel = (val,pct,cfg) => (
                            <td style={{ padding:'8px 6px', textAlign:'center' }}>
                              <Typography sx={{ fontSize:'0.76rem', fontWeight:700, color:cfg.text }}>{pct}%</Typography>
                              <Typography sx={{ fontSize: '0.72rem', color:'#4B5563' }}>{val} ME</Typography>
                            </td>
                          );
                          const pct = (n) => r.totalME>0 ? Math.round(n*1000/r.totalME)/10 : 0;
                          const sel = selEvol?.id===r.idEstablecimiento;
                          return (
                            <React.Fragment key={i}>
                              <tr onClick={()=>cargarEvolucionIpress(r.idEstablecimiento, r.nombreEstablecimiento, !!r.esHospital)}
                                style={{ borderBottom:sel?'none':`1px solid ${IOS.separator}`, backgroundColor:sel?`${IOS.blue}08`:IOS.surface, cursor:'pointer', transition:'background 0.1s' }}
                                onMouseEnter={e=>{ if(!sel) e.currentTarget.style.backgroundColor=IOS.bg; }}
                                onMouseLeave={e=>{ e.currentTarget.style.backgroundColor=sel?`${IOS.blue}08`:IOS.surface; }}>
                                <td style={{ padding:'8px 10px', fontWeight:700, color:'#1F2937', whiteSpace:'nowrap' }}>{r.nombreEstablecimiento}</td>
                                <td style={{ padding:'8px 6px', textAlign:'center', color:'#374151' }}>{r.totalProductos}</td>
                                <td style={{ padding:'8px 6px', textAlign:'center', fontWeight:700, color:'#1F2937' }}>{r.totalME}</td>
                                <td style={{ padding:'8px 6px', textAlign:'center', color:'#374151' }}>{r.totalProductos>0?Math.round(r.totalME*100/r.totalProductos):0}%</td>
                                {Cel(r.sobrestock, pct(r.sobrestock), IND_CFG.SOBRESTOCK)}
                                {Cel(r.normostock, pct(r.normostock), IND_CFG.NORMOSTOCK)}
                                {Cel(r.substock, pct(r.substock), IND_CFG.SUBSTOCK)}
                                {Cel(r.desabastecido, pct(r.desabastecido), IND_CFG.DESABASTECIDO)}
                                {Cel(r.sinRotacion, pct(r.sinRotacion), IND_CFG['SIN ROTACION'])}
                                {Cel(r.sinConsumo, pct(r.sinConsumo), IND_CFG['SIN CONSUMO'])}
                                <td style={{ padding:'8px 10px', textAlign:'center' }}>
                                  <Box sx={{ display:'inline-flex', px:1, py:0.3, borderRadius:'20px', bgcolor:`${dc.color}15`, border:`1px solid ${dc.color}40` }}>
                                    <Typography sx={{ fontSize:'0.78rem', fontWeight:800, color:dc.color }}>{r.disp_ME_2meses.toFixed(1)}%</Typography>
                                  </Box>
                                </td>
                              </tr>
                              {sel && (
                                <tr style={{ borderBottom:`1px solid ${IOS.separator}` }}>
                                  <td colSpan={11} style={{ padding:0, backgroundColor:IOS.bg }}>
                                    <Box sx={{ px:3, py:2 }}>{renderEvolucion()}</Box>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </Box>
                </Box>
              </Box>
            );
          })()}
        </Box>
      </Box>

      {/* ── MODAL medicamento ── */}
      <Dialog open={modal} onClose={()=>setModal(false)} maxWidth="lg" fullWidth aria-labelledby="indicator-medicine-title"
        PaperProps={{ className:'pharmacy-page indicadores-page', sx:{ borderRadius:'24px', bgcolor:IOS.bg, boxShadow:'0 24px 60px rgba(0,0,0,0.18)', overflow:'hidden', m:{ xs:1, sm:2 } } }}>

        {/* Header modal */}
        <Box className="pharmacy-layout" sx={{ bgcolor:IOS.surface, px:3, pt:2.5, pb:2,
          borderBottom:`1px solid ${IOS.separator}`,
          display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:2 }}>
          <Box>
            <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:IOS.label2,
              letterSpacing:'0.07em', textTransform:'uppercase', mb:0.3 }}>Evolución Anual</Typography>
            <Typography component="h2" id="indicator-medicine-title" sx={{ fontSize:'0.95rem', fontWeight:700, color:IOS.label, lineHeight:1.2, mb:0.3 }}>
              {medSel?.nombre}
            </Typography>
            <Typography sx={{ fontSize:'0.72rem', color:IOS.label2, fontFamily:'monospace' }}>{medSel?.codigo}</Typography>
          </Box>
          <Box className="pharmacy-layout pharmacy-action" component="button" aria-label="Cerrar evolución del medicamento" onClick={()=>setModal(false)}
            sx={{ width:32, height:32, borderRadius:'50%', border:'none', flexShrink:0,
              bgcolor:'#E5E5EA', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
              transition:'all 0.15s', '&:hover':{ bgcolor:'#D1D1D6' } }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={IOS.label} strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </Box>
        </Box>

        <Box sx={{ overflow:'auto', maxHeight:'75vh', px:{ xs:1.5, sm:2.5 }, py:2,
          '&::-webkit-scrollbar':{ width:4 }, '&::-webkit-scrollbar-thumb':{ bgcolor:IOS.separator, borderRadius:4 } }}>
          {loadModal
            ? <Box sx={{ py:8, textAlign:'center' }}><CircularProgress sx={{ color:IOS.blue }} /></Box>
            : dataMed.length>0
              ? (() => {
                  const ultimo = dataMed[dataMed.length-1];
                  const msdU = Number(ultimo?.msd||ultimo?.MSD||0);
                  const indU = ultimo?.indicador_DIGEMID||ultimo?.Indicador_DIGEMID||'—';
                  const cfgU = getInd(indU);
                  const chartD = dataMed.map(d=>({ periodo:fmtP(d.annoMes||d.AnnoMes||''), stock:Number(d.stock_Final||d.Stock_Final||0), cpma:Number(d.cpma||d.CPMA||0), msd:Number(d.msd||d.MSD||0), ind:d.indicador_DIGEMID||d.Indicador_DIGEMID||'' }));
                  return (
                    <Box>
                      {/* KPIs modal */}
                      <Box className="pharmacy-layout" sx={{ display:'grid', gridTemplateColumns:{ xs:'repeat(2,1fr)', sm:'repeat(4,1fr)' }, gap:1.5, mb:2 }}>
                        {[
                          ['Último Stock', Number(ultimo?.stock_Final||ultimo?.Stock_Final||0).toFixed(0), IOS.blue,
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>],
                          ['CPMA Actual',  Number(ultimo?.cpma||ultimo?.CPMA||0).toFixed(2), IOS.purple,
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M18 20V10M12 20V4M6 20v-6"/></svg>],
                          ['MSD Actual',   msdU.toFixed(2), msdU<1?IOS.red:msdU<2?IOS.orange:IOS.green,
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>],
                        ].map(([lbl,val,col,icon])=>(
                          <Box className="pharmacy-card" key={lbl} sx={{ bgcolor:IOS.surface, borderRadius:'16px', p:1.8,
                            boxShadow:'0 1px 4px rgba(0,0,0,0.06)', border:`1px solid ${IOS.separator}` }}>
                            <Box className="pharmacy-layout" sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:1 }}>
                              <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:IOS.label2,
                                letterSpacing:'0.07em', textTransform:'uppercase' }}>{lbl}</Typography>
                              <Box className="pharmacy-layout" sx={{ width:26, height:26, borderRadius:'8px', bgcolor:`${col}12`, color:col,
                                display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{icon}</Box>
                            </Box>
                            <Typography sx={{ fontSize:'1.3rem', fontWeight:800, color:col, lineHeight:1 }}>{val}</Typography>
                          </Box>
                        ))}
                        <Box className="pharmacy-layout pharmacy-card" sx={{ bgcolor:IOS.surface, borderRadius:'16px', p:1.8,
                          boxShadow:'0 1px 4px rgba(0,0,0,0.06)', border:`1px solid ${IOS.separator}`,
                          display:'flex', flexDirection:'column', justifyContent:'space-between' }}>
                          <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:IOS.label2,
                            letterSpacing:'0.07em', textTransform:'uppercase', mb:1 }}>Indicador Actual</Typography>
                          <IndPill valor={indU} />
                        </Box>
                      </Box>

                      {/* Gráfico modal */}
                      <Box sx={{ mb:2 }}>
                        <ChartCard title="Stock vs CPMA por Período" sub="Barras coloreadas según el indicador DIGEMID de cada mes">
                          <Box sx={{ height:260 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <ComposedChart accessibilityLayer={true} data={chartD} margin={{ top:8, right:16, left:0, bottom:0 }}>
                                <CartesianGrid horizontal={true} strokeDasharray="3 5" stroke="#EDF1F6" vertical={false}/>
                                <XAxis dataKey="periodo" fontSize={10} stroke={IOS.separator} tickLine={false} axisLine={false}/>
                                <YAxis yAxisId="l" fontSize={10} stroke={IOS.separator} tickLine={false} axisLine={false}/>
                                <YAxis yAxisId="r" orientation="right" fontSize={10} stroke={IOS.separator} tickLine={false} axisLine={false}/>
                                <RTooltip animationDuration={0} content={<TT/>}/>
                                <defs><linearGradient id="indicator-medicine" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={IOS.blue} stopOpacity={0.14}/><stop offset="100%" stopColor={IOS.blue} stopOpacity={0}/></linearGradient></defs><Area yAxisId="r" dataKey="cpma" type="monotone" stroke="none" fill="url(#indicator-medicine)" tooltipType="none" legendType="none" isAnimationActive={!reducedMotion} animationDuration={850} animationEasing="ease-out"/>
                                <Legend wrapperStyle={{ fontSize:'0.72rem', paddingTop:8 }}/>
                                <Bar maxBarSize={26} isAnimationActive={!reducedMotion} animationEasing="ease-out" animationDuration={850} yAxisId="l" dataKey="stock" name="Stock Final" fill={IOS.blue} radius={[6,6,0,0]}>
                                  {dataMed.map((d,i)=><Cell key={i} fill={getInd(d.indicador_DIGEMID||d.Indicador_DIGEMID||'').dot}/>)}
                                </Bar>
                                <Line isAnimationActive={!reducedMotion} animationEasing="ease-out" animationDuration={850} yAxisId="r" type="monotone" dataKey="cpma" name="CPMA" stroke={IOS.blue} strokeWidth={3} dot={{ fill:IOS.blue, r:3.5, stroke:'white', strokeWidth:1.5 }}/>
                                <Line isAnimationActive={!reducedMotion} animationEasing="ease-out" animationDuration={850} yAxisId="r" type="monotone" dataKey="msd"  name="MSD"  stroke={IOS.orange} strokeWidth={3} strokeDasharray="5 5" dot={{ fill:IOS.orange, r:3 }}/>
                              </ComposedChart>
                            </ResponsiveContainer>
                          </Box>
                          <Box className="pharmacy-layout" sx={{ display:'flex', gap:1.5, justifyContent:'center', mt:1.5, flexWrap:'wrap' }}>
                            {Object.entries(IND_CFG).filter(([k])=>k!=='NO APLICA').map(([k,c])=>(
                              <Box className="pharmacy-layout" key={k} sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
                                <Box sx={{ width:8, height:8, borderRadius:'2px', bgcolor:c.dot }} />
                                <Typography sx={{ fontSize: '0.72rem', color:IOS.label2 }}>{k.charAt(0)+k.slice(1).toLowerCase()}</Typography>
                              </Box>
                            ))}
                          </Box>
                        </ChartCard>
                      </Box>

                      {/* Tabla modal */}
                      <Box sx={{ bgcolor:IOS.surface, borderRadius:'14px', border:`1px solid ${IOS.separator}`, overflow:'hidden' }}>
                        <Box className="pharmacy-scroll" tabIndex={0} role="region" aria-label="Tabla de resultados, desplazamiento horizontal" sx={{ overflowX:'auto', maxHeight:320,
                          '&::-webkit-scrollbar':{ width:4 }, '&::-webkit-scrollbar-thumb':{ bgcolor:IOS.separator, borderRadius:4 } }}>
                          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.75rem' }}>
                            <thead>
                              <tr>
                                {['Período','Stock','CPMA','MSD','Indicador'].map((h,i)=>(
                                  <th key={i} style={{ padding:'9px 12px', textAlign:i===0||i===4?'center':'right',
                                    fontSize: '0.72rem', fontWeight:700, color:IOS.label2, letterSpacing:'0.06em',
                                    textTransform:'uppercase', backgroundColor:IOS.surface,
                                    borderBottom:`1px solid ${IOS.separator}`, position:'sticky', top:0 }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {dataMed.map((row,idx)=>{
                                const am=row.annoMes||row.AnnoMes||'';
                                const ind=row.indicador_DIGEMID||row.Indicador_DIGEMID||'';
                                const msdR=Number(row.msd||row.MSD||0);
                                return (
                                  <tr key={idx} style={{ borderBottom:`1px solid ${IOS.separator}`, backgroundColor:IOS.surface, transition:'background 0.1s' }}
                                    onMouseEnter={e=>e.currentTarget.style.backgroundColor=IOS.bg}
                                    onMouseLeave={e=>e.currentTarget.style.backgroundColor=IOS.surface}>
                                    <td style={{ padding:'9px 12px', textAlign:'center', fontWeight:600, color:IOS.label }}>{fmtP(am)}</td>
                                    <td style={{ padding:'9px 12px', textAlign:'right', fontWeight:700, color:IOS.label }}>{Number(row.stock_Final||row.Stock_Final||0).toFixed(0)}</td>
                                    <td style={{ padding:'9px 12px', textAlign:'right', color:IOS.label }}>{Number(row.cpma||row.CPMA||0).toFixed(2)}</td>
                                    <td style={{ padding:'9px 12px', textAlign:'right', fontWeight:600, color:msdR<1?IOS.red:msdR<2?IOS.orange:IOS.green }}>{msdR.toFixed(2)}</td>
                                    <td style={{ padding:'9px 12px', textAlign:'center' }}><IndPill valor={ind} sm /></td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </Box>
                      </Box>
                    </Box>
                  );
                })()
              : <Box sx={{ py:8, textAlign:'center' }}><Typography sx={{ color:IOS.label2, fontSize:'0.88rem' }}>Sin datos disponibles</Typography></Box>
          }
        </Box>
      </Dialog>

      {/* ── MODAL AUDITORÍA ICI ── */}
      <Dialog open={modalAuditoria} onClose={()=>setModalAuditoria(false)}
        PaperProps={{ sx:{ borderRadius:'18px', bgcolor:IOS.bg, boxShadow:'0 24px 60px rgba(0,0,0,0.18)',
          overflow:'hidden', m:{ xs:1, sm:2 }, width:{ xs:'calc(100% - 16px)', md:'92vw' },
          maxWidth:{ xs:'calc(100% - 16px)', md:1400 },
          maxHeight:'90vh', display:'flex', flexDirection:'column' } }}>

        <Box className="pharmacy-layout" sx={{ bgcolor:IOS.surface, px:3, pt:2.5, pb:2, borderBottom:`1px solid ${IOS.separator}`,
          display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:2, flexShrink:0 }}>
          <Box>
            <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:IOS.label2, letterSpacing:'0.07em', textTransform:'uppercase', mb:0.3 }}>
              Verificación de Calidad de Datos
            </Typography>
            <Typography sx={{ fontSize:'0.95rem', fontWeight:700, color:IOS.label }}>Auditoría ICI</Typography>
            <Typography sx={{ fontSize:'0.72rem', color:IOS.label2, mt:0.2 }}>
              {ipressActual?.nombre||'Toda la Unidad Ejecutora'} · períodos hasta {fmtP(periodo)}
            </Typography>
          </Box>
          <Box className="pharmacy-layout pharmacy-action" component="button" onClick={()=>setModalAuditoria(false)}
            sx={{ width:32, height:32, borderRadius:'50%', border:'none', flexShrink:0,
              bgcolor:'#E5E5EA', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
              transition:'all 0.15s', '&:hover':{ bgcolor:'#D1D1D6' } }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={IOS.label} strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </Box>
        </Box>

        <Box sx={{ flex:1, overflowY:'auto', overflowX:'hidden', px:{ xs:1.5, sm:2.5 }, py:2,
          '&::-webkit-scrollbar':{ width:4 }, '&::-webkit-scrollbar-thumb':{ bgcolor:IOS.separator, borderRadius:4 } }}>

          {loadAuditoria
            ? <Box sx={{ py:8, textAlign:'center' }}><CircularProgress sx={{ color:IOS.blue }} /></Box>
            : <>
                {auditConsultado && totalHallazgos>0 && (
                  <Box className="pharmacy-layout" sx={{ mb:2, p:1.8, borderRadius:'14px', bgcolor:'#FFF8E6', border:'1px solid #FFE9A0',
                    display:'flex', alignItems:'flex-start', gap:1.2 }}>
                    <Box className="pharmacy-layout" sx={{ width:26, height:26, borderRadius:'8px', bgcolor:'#FFE9A0', flexShrink:0,
                      display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8B6914" strokeWidth="2" strokeLinecap="round">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                      </svg>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize:'0.8rem', fontWeight:700, color:'#8B6914', mb:0.3 }}>
                        Se encontraron {totalHallazgos} inconsistencia{totalHallazgos===1?'':'s'}
                      </Typography>
                      <Typography sx={{ fontSize:'0.74rem', color:'#8B6914' }}>
                        Esto puede afectar la precisión de los indicadores mostrados en la pestaña Detalles y Dashboard.
                        Revisa antes de tomar decisiones con esta información.
                      </Typography>
                    </Box>
                  </Box>
                )}
                {auditConsultado && totalHallazgos===0 && (
                  <Box className="pharmacy-layout" sx={{ mb:2, p:1.8, borderRadius:'14px', bgcolor:'#EDFAF2', border:'1px solid #B7EFD0',
                    display:'flex', alignItems:'center', gap:1.2 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1A6B3C" strokeWidth="2" strokeLinecap="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                    <Typography sx={{ fontSize:'0.8rem', fontWeight:700, color:'#1A6B3C' }}>
                      Sin inconsistencias — los datos están completos y sin duplicados
                    </Typography>
                  </Box>
                )}

                {/* Sub-tabs */}
                <Box className="pharmacy-layout" sx={{ display:'flex', gap:0.5, bgcolor:IOS.surface, borderRadius:'10px', p:0.4,
                  width:'fit-content', mb:2, boxShadow:'0 1px 3px rgba(0,0,0,0.07)' }}>
                  {[`Períodos Faltantes (${auditFaltantes.length})`, `Anomalías (${auditAnomalias.length})`].map((t,i)=>(
                    <Box key={i} onClick={()=>setAuditTab(i)}
                      sx={{ px:2, py:0.7, borderRadius:'8px', cursor:'pointer',
                        bgcolor:auditTab===i?IOS.bg:'transparent',
                        boxShadow:auditTab===i?'0 1px 3px rgba(0,0,0,0.1)':'none', transition:'all 0.2s' }}>
                      <Typography sx={{ fontSize:'0.76rem', fontWeight:auditTab===i?700:500,
                        color:auditTab===i?IOS.label:IOS.label2, whiteSpace:'nowrap' }}>{t}</Typography>
                    </Box>
                  ))}
                </Box>

                {/* Períodos faltantes */}
                {auditTab===0 && (
                  auditFaltantes.length===0
                    ? <Box sx={{ py:6, textAlign:'center' }}><Typography sx={{ color:IOS.label2, fontSize:'0.85rem' }}>Ningún establecimiento tiene períodos faltantes</Typography></Box>
                    : <Box className="pharmacy-layout" sx={{ display:'grid', gridTemplateColumns:{ xs:'1fr', md:'1fr 1fr' }, gap:1.2 }}>
                    {auditFaltantes.map((f,i)=>(
                        <Box key={i} sx={{ bgcolor:IOS.surface, borderRadius:'14px', p:2, minWidth:0, border:`1px solid ${IOS.separator}`,
                          gridColumn:{ md: auditFaltantes.length===1?'1 / -1':'auto' } }}>
                          <Box className="pharmacy-layout" sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:1, mb:1 }}>
                            <Typography sx={{ fontWeight:700, fontSize:'0.84rem', color:IOS.label }}>{f.establecimiento}</Typography>
                            <Box sx={{ display:'inline-flex', px:1, py:0.3, borderRadius:'20px', bgcolor:'#FEE2E2', border:'1px solid #FCA5A5', flexShrink:0 }}>
                              <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:'#991B1B' }}>{f.mesesFaltantes} de {f.mesesEsperados} meses</Typography>
                            </Box>
                          </Box>
                          <Box className="pharmacy-layout" sx={{ display:'flex', flexWrap:'wrap', gap:0.6 }}>
                            {(f.periodosFaltantes||'').split(',').map(p=>p.trim()).filter(Boolean).map((p,j)=>(
                              <Box key={j} sx={{ px:0.9, py:0.25, borderRadius:'8px', bgcolor:IOS.bg, border:`1px solid ${IOS.separator}` }}>
                                <Typography sx={{ fontSize: '0.72rem', fontFamily:'monospace', color:IOS.label2 }}>{fmtP(p)}</Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                    ))}
                    </Box>
                )}

                {/* Anomalías */}
                {auditTab===1 && (
                  auditAnomalias.length===0
                    ? <Box sx={{ py:6, textAlign:'center' }}><Typography sx={{ color:IOS.label2, fontSize:'0.85rem' }}>Sin registros duplicados o modificados</Typography></Box>
                    : <Box className="pharmacy-layout" sx={{ display:'grid', gridTemplateColumns:{ xs:'1fr', md:'1fr 1fr' }, gap:1.2 }}>
                    {auditAnomalias.map((a,i)=>{
                        const cambio = a.estado==='CAMBIÓ';
                        return (
                          <Box key={i} sx={{ bgcolor:IOS.surface, borderRadius:'14px', p:2, minWidth:0,
                            border:`1px solid ${cambio?'#FCA5A5':IOS.separator}`,
                            gridColumn:{ md: auditAnomalias.length===1?'1 / -1':'auto' } }}>
                            <Box className="pharmacy-layout" sx={{ display:'flex', flexWrap:{ xs:'wrap', sm:'nowrap' }, justifyContent:'space-between', alignItems:'flex-start', gap:1, mb:0.6 }}>
                              <Box sx={{ minWidth:0, flex:1 }}>
                                <Typography sx={{ fontWeight:700, fontSize:'0.82rem', color:IOS.label, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.producto||'—'}</Typography>
                                <Typography sx={{ fontSize: '0.72rem', color:IOS.label2, fontFamily:'monospace', mt:0.1 }}>{a.codigoMed}</Typography>
                                <Typography sx={{ fontSize: '0.72rem', color:IOS.label2, mt:0.2 }}>
                                  Período {fmtP(a.annomes)} · Lote {a.medlote||'—'} · RS {a.medregsan||'—'} · Tipo {a.tipsum||'—'}
                                </Typography>
                                {auditEsUE && (
                                  <Typography sx={{ fontSize:'0.7rem', fontWeight:700, color:IOS.blue, mt:0.3 }}>{a.establecimiento}</Typography>
                                )}
                              </Box>
                              <Box sx={{ display:'inline-flex', alignItems:'center', gap:0.5, px:0.8, py:0.2, borderRadius:'20px',
                                bgcolor:cambio?'#FEE2E2':'#F3F4F6', border:`1px solid ${cambio?'#FCA5A5':'#D1D5DB'}`, flexShrink:0 }}>
                                <Box sx={{ width:5, height:5, borderRadius:'50%', bgcolor:cambio?IOS.red:'#A6B1C2' }} />
                                <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:cambio?'#991B1B':'#4B5563' }}>{a.estado}</Typography>
                              </Box>
                            </Box>

                            <Box className="pharmacy-layout" sx={{ display:'grid', gridTemplateColumns:{ xs:'1fr', sm:'1fr 1fr' }, gap:{ xs:1.2, sm:1 }, p:1.2, bgcolor:IOS.bg, borderRadius:'10px', mb:cambio?1:0 }}>
                              <Box>
                                <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:IOS.label2, textTransform:'uppercase', mb:0.3 }}>1ª versión</Typography>
                                <Typography sx={{ fontSize: '0.72rem', color:IOS.label2, mb:0.3 }}>{a.primeraVersion}</Typography>
                                <Box className="pharmacy-layout" sx={{ display:'flex', gap:1.5 }}>
                                  <Typography sx={{ fontSize:'0.75rem', fontWeight:700, color:IOS.label }}>Stock {a.stockPrimeraVersion.toFixed(0)}</Typography>
                                  <Typography sx={{ fontSize:'0.75rem', fontWeight:700, color:IOS.label }}>Total {a.totalPrimeraVersion.toFixed(2)}</Typography>
                                </Box>
                              </Box>
                              <Box sx={{ borderLeft:{ xs:'none', sm:`1px dashed ${IOS.separator}` }, borderTop:{ xs:`1px dashed ${IOS.separator}`, sm:'none' }, pl:{ xs:0, sm:1 }, pt:{ xs:1, sm:0 } }}>
                                <Typography sx={{ fontSize: '0.72rem', fontWeight:700, color:IOS.label2, textTransform:'uppercase', mb:0.3 }}>Última versión</Typography>
                                <Typography sx={{ fontSize: '0.72rem', color:IOS.label2, mb:0.3 }}>{a.ultimaVersion}</Typography>
                                <Box className="pharmacy-layout" sx={{ display:'flex', gap:1.5 }}>
                                  <Typography sx={{ fontSize:'0.75rem', fontWeight:700, color:cambio?IOS.red:IOS.label }}>Stock {a.stockUltimaVersion.toFixed(0)}</Typography>
                                  <Typography sx={{ fontSize:'0.75rem', fontWeight:700, color:cambio?IOS.red:IOS.label }}>Total {a.totalUltimaVersion.toFixed(2)}</Typography>
                                </Box>
                              </Box>
                            </Box>

                            {cambio && (
                              <Typography sx={{ fontSize:'0.7rem', fontWeight:600, color:'#991B1B', mb:0.6 }}>
                                Δ Stock {a.diferenciaStock>0?'+':''}{a.diferenciaStock.toFixed(0)} · Δ Total {a.diferenciaTotal>0?'+':''}{a.diferenciaTotal.toFixed(2)}
                              </Typography>
                            )}
                            <Typography sx={{ fontSize: '0.72rem', color:IOS.label2 }}>
                              {a.registros} versiones registradas · {a.tiempoTranscurrido}
                            </Typography>
                          </Box>
                        );
                    })}
                    </Box>
                )}
              </>
          }
        </Box>
      </Dialog>

      <ModalExportarICI
        open={modalExpICI}
        onClose={()=>setModalExpICI(false)}
        total={tablaF.length}
        onVerPdf={verPdfICI}
        onExportarExcel={exportarExcelICI}
        exportando={expExcelICI}
      />
      <ModalPdfVisorICI
        open={modalPdfICI}
        onClose={closePdfICI}
        pdfUrl={pdfUrlICI}
        cargando={cargPdfICI}
        titulo={`Disponibilidad ICI — ${vistaTipo==='ME'?'Medicamentos Esenciales':'Catálogo Completo'}`}
        subtitulo={`${ipressActual?.nombre||''} · ${fmtP(periodo)}`}
        nombreDescarga={`ICI_${periodo}_${vistaTipo}.pdf`}
      />
    </>
  );
};

export default Indicadores;
